import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { useLanguage } from '../context/LanguageContext';
import {
  Box,
  Grid,
  CircularProgress,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  FormHelperText,
  DialogContentText,
  Tooltip,
  Step,
  StepLabel,
  Stepper,
  Tabs,
  Tab,
  InputAdornment,
  OutlinedInput,
  Checkbox,
  ListItemText,
  useMediaQuery,
  useTheme,
  Autocomplete,
  Rating,
  Collapse,
} from '@mui/material';
import {
  Delete,
  Edit,
  Add,
  FilterAltOff,
  CheckCircle,
  Cancel,
  Phone,
  Email,
  Language,
  Apps as AppsIcon,
  FileCopy as FileCopyIcon,
  Gavel as GavelIcon,
  ConfirmationNumber as TicketIcon,
  WhatsApp,
  LocationOn,
  AccessTime,
  Settings as SettingsIcon,
  Info as InfoIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  Map as MapIcon,
  List as ListIcon,
  Warning as InactiveIcon,
  CloudUpload,
  AddPhotoAlternate,
  CloseRounded,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Event as CalendarIcon,
  Business as BusinessIcon,
  Forum as ForumIcon,
  Chat as ChatIcon,
  Campaign,
  Smartphone as SmartphoneIcon,
  Star as StarIcon,
  StarHalf as StarHalfIcon,
  StarBorder as StarOutlineIcon,
  Visibility,
  ExpandMore as ExpandMoreIcon,
  ArrowForward as ArrowForwardIcon,
  ViewAgenda as CardViewIcon,
  Videocam as VideocamIcon,
  Storefront as StorefrontIcon,
  ChevronRight as ChevronRightIcon,
  Person as PersonIcon,
  Directions as DirectionsIcon,
  Shield as ShieldIcon,
  Flag as FlagIcon,
  Share as ShareIcon,
  ChevronLeft as ChevronLeftIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  Verified as VerifiedBadgeIcon,
} from '@mui/icons-material';
import { contactsAPI, subCategoriesAPI, districtsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

/* global globalThis */

/** Read JWT payload (no signature verify) — category may live only in token claims */
function normalizeJwtBase64UrlSegment(segment) {
  let base64 = segment.replaceAll('-', '+').replaceAll('_', '/');
  while (base64.length % 4) base64 += '=';
  return base64;
}

function encodeBinaryCharForUriComponent(char) {
  const hex = char.codePointAt(0).toString(16).padStart(2, '0');
  return `%${hex.slice(-2)}`;
}

function parseJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const base64 = normalizeJwtBase64UrlSegment(parts[1]);
    const json = decodeURIComponent(
      Array.prototype.map.call(atob(base64), encodeBinaryCharForUriComponent).join('')
    );
    return JSON.parse(json);
  } catch {
    try {
      return JSON.parse(atob(normalizeJwtBase64UrlSegment(parts[1])));
    } catch {
      return null;
    }
  }
}

/** API returns { success, error: { code, message } } or { message } on failure */
function getContactsApiErrorMessage(data) {
  if (!data) return 'Unknown error';
  if (typeof data.error === 'string') return data.error;
  if (data.error?.message) return data.error.message;
  if (data.message) return data.message;
  return 'Unknown error';
}

function parseIdsListField(v) {
  if (v == null) return [];
  if (Array.isArray(v)) {
    return v.map(Number).filter((n) => Number.isFinite(n) && n > 0);
  }
  if (typeof v === 'string' && v.trim()) {
    return v
      .split(',')
      .map((x) => Number.parseInt(String(x).trim(), 10))
      .filter((n) => !Number.isNaN(n) && n > 0);
  }
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? [n] : [];
}

/** Helper function to extract star rating info (averageRating & totalRating) from contact object */
function getContactRatingInfo(contact) {
  if (!contact) return { averageRating: 0, totalRating: 0, hasRating: false };

  let averageRating = 0;
  let totalRating = 0;

  if (contact.contactRating && typeof contact.contactRating === 'object') {
    if (contact.contactRating.averageRating !== undefined && contact.contactRating.averageRating !== null) {
      averageRating = parseFloat(contact.contactRating.averageRating) || 0;
    } else if (contact.contactRating.rating !== undefined && contact.contactRating.rating !== null) {
      averageRating = parseFloat(contact.contactRating.rating) || 0;
    }

    if (contact.contactRating.totalRating !== undefined && contact.contactRating.totalRating !== null) {
      totalRating = parseInt(contact.contactRating.totalRating, 10) || 0;
    }
  }

  if (!averageRating) {
    if (contact.averageRating !== undefined && contact.averageRating !== null) {
      averageRating = parseFloat(contact.averageRating) || 0;
    } else if (contact.rating !== undefined && contact.rating !== null) {
      averageRating = parseFloat(contact.rating) || 0;
    }
  }

  if (!totalRating) {
    if (contact.totalRating !== undefined && contact.totalRating !== null) {
      totalRating = parseInt(contact.totalRating, 10) || 0;
    }
  }

  return {
    averageRating: Math.round(averageRating * 10) / 10,
    totalRating,
    hasRating: averageRating > 0 || totalRating > 0
  };
}

/** Helper function to render 5 stars (guaranteeing 4 full gold stars + 1 half gold star for 4.5) */
function renderFiveStarRating(rating, size = 15) {
  const num = parseFloat(rating) || 0;
  const fullStars = Math.floor(num);
  const remainder = num - fullStars;
  const hasHalfStar = remainder >= 0.25 && remainder <= 0.75;
  const effectiveFullCount = remainder > 0.75 ? fullStars + 1 : fullStars;

  const stars = [];
  for (let i = 1; i <= 5; i++) {
    if (i <= effectiveFullCount) {
      stars.push(<StarIcon key={i} sx={{ color: '#F59E0B', fontSize: size, display: 'block' }} />);
    } else if (i === effectiveFullCount + 1 && hasHalfStar) {
      stars.push(<StarHalfIcon key={i} sx={{ color: '#F59E0B', fontSize: size, display: 'block' }} />);
    } else {
      stars.push(<StarOutlineIcon key={i} sx={{ color: '#FCD34D', fontSize: size, display: 'block' }} />);
    }
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.1, lineHeight: 1 }}>
      {stars}
    </Box>
  );
}

function parseRawSubCategoryIdList(raw) {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map(Number).filter((n) => !Number.isNaN(n) && n > 0);
  }
  return String(raw)
    .split(',')
    .map((x) => Number.parseInt(String(x).trim(), 10))
    .filter((n) => !Number.isNaN(n) && n > 0);
}

function parseSubCategoryIdsFromMixedArray(subs) {
  if (!Array.isArray(subs) || subs.length === 0) return [];
  if (subs.every((x) => typeof x === 'string')) {
    return subs
      .map((x) => {
        const s = String(x).trim().replace(/^#/, '');
        const n = Number.parseInt(s, 10);
        return Number.isFinite(n) ? n : Number.NaN;
      })
      .filter((n) => !Number.isNaN(n));
  }
  return subs
    .map((x) => {
      if (x && typeof x === 'object') {
        const id = Number(x.id ?? x.subCategoryId ?? x.SubCategoryId);
        return Number.isFinite(id) ? id : Number.NaN;
      }
      const s = String(x).trim().replace(/^#/, '');
      const n = Number.parseInt(s, 10);
      return Number.isFinite(n) ? n : Number.NaN;
    })
    .filter((n) => !Number.isNaN(n));
}

/**
 * Subcategory ids may arrive as subCategoryIds / serviceSubCategoryIds, subCategoryId ("1,3,5"),
 * or subCategories as ["#1","#3"], display name strings, or [{ id, type }, …] from the API.
 */
function parseSubCategoryIdsFromContact(contact) {
  if (!contact || typeof contact !== 'object') return [];
  const merged = [
    ...new Set([
      ...parseIdsListField(contact.serviceSubCategoryIds),
      ...parseIdsListField(contact.subCategoryIds),
    ]),
  ];
  if (merged.length > 0) return merged;
  const ids = parseRawSubCategoryIdList(contact.subCategoryId);
  if (ids.length > 0) return ids;
  return parseSubCategoryIdsFromMixedArray(contact.subCategories);
}

/** Label for a subcategory row from GET /subcategories or embedded contact rows (`{ id, type }`). */
function getSubcategoryRowLabel(s, id) {
  if (!s || typeof s !== 'object') return `Subcategory ${id}`;
  const raw =
    s.name_EN ??
    s.name ??
    s.title_EN ??
    s.title ??
    s.type ??
    s.subType ??
    s.label ??
    s.title_DE;
  if (raw != null && String(raw).trim()) return String(raw).trim();
  return `Subcategory ${id}`;
}

function normalizeBannerImagesArray(contact) {
  if (Array.isArray(contact.bannerImages)) return contact.bannerImages;
  if (typeof contact.bannerImages === 'string') {
    return contact.bannerImages.split(',').map((s) => s.trim()).filter(Boolean);
  }
  if (typeof contact.imageUrls === 'string') {
    return contact.imageUrls.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return contact.imageUrls || [];
}

function resolveOfflineBookingEnabled(value) {
  if (value === undefined) return true;
  return Boolean(value);
}

function parseStringArrayField(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v.map(String).filter(Boolean);
  if (typeof v === 'string') {
    try {
      const parsed = JSON.parse(v);
      if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
    } catch { }
    return v.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

const PRICING_PLANS = [
  {
    title: 'Student Health Insurance',
    badge: 'Most Popular',
    description: 'Statutory & expat health cover fully compliant with German student visa requirements & university enrolment.',
    price: '€125',
    period: '/ month',
    hint: 'Starts from €125/mo • Free incoming travel insurance included',
    highlights: [
      'Direct University Certificate (M10)',
      '100% Visa Approved by Embassy',
      'Free English Customer Support',
      'Dental & Routine Checkups Included',
    ],
  },
  {
    title: 'Expat Professional Plan',
    badge: 'Flexible',
    description: 'Private expat health insurance for freelancers and job seekers.',
    price: '€180',
    period: '/ month',
    hint: 'Cancel anytime on monthly basis',
    highlights: [
      'English speaking customer service',
      'Fast digital claims processing',
      'No waiting period',
    ],
  },
];

function parseSinglePricingPlan(item) {
  if (!item) return null;
  if (typeof item === 'object' && !Array.isArray(item)) {
    return {
      title: item.title || '',
      badge: item.badge || '',
      description: item.description || '',
      price: item.price || '',
      period: item.period || '',
      hint: item.hint || '',
      highlights: parseStringArrayField(item.highlights),
    };
  }
  if (typeof item === 'string') {
    try {
      const parsed = JSON.parse(item);
      if (parsed && typeof parsed === 'object') return parseSinglePricingPlan(parsed);
    } catch { }
    return { title: item, badge: '', description: '', price: '', period: '', hint: '', highlights: [] };
  }
  return null;
}

function parsePricingInformationField(v) {
  const defaultObj = {
    headerTitle: '',
    headerDescription: '',
    plans: [],
  };

  if (!v) return defaultObj;

  if (typeof v === 'string') {
    try {
      const parsed = JSON.parse(v);
      if (parsed) return parsePricingInformationField(parsed);
    } catch { }
    const items = v.split(',').map((s) => s.trim()).filter(Boolean);
    return {
      ...defaultObj,
      plans: items.map((t) => ({ title: t, badge: '', description: '', price: '', period: '', hint: '', highlights: [] })),
    };
  }

  if (Array.isArray(v)) {
    return {
      ...defaultObj,
      plans: v.map(parseSinglePricingPlan).filter(Boolean),
    };
  }

  if (typeof v === 'object') {
    const rawPlans = Array.isArray(v.plans) ? v.plans : (Array.isArray(v.pricingPlans) ? v.pricingPlans : []);
    return {
      headerTitle: v.headerTitle || '',
      headerDescription: v.headerDescription || '',
      plans: rawPlans.map(parseSinglePricingPlan).filter(Boolean),
    };
  }

  return defaultObj;
}
/** Normalize one contact from list or GET /customer/contacts/{id} for form binding */
function transformContactFromApi(contact) {
  if (!contact || typeof contact !== 'object') return contact;
  const subIds = parseSubCategoryIdsFromContact(contact);
  return {
    ...contact,
    ...(subIds.length > 0 ? { subCategoryId: subIds } : {}),
    supportLanguages:
      typeof contact.supportLanguages === 'string'
        ? contact.supportLanguages.split(',').map((s) => s.trim())
        : contact.supportLanguages,
    clientTypes:
      typeof contact.clientTypes === 'string'
        ? contact.clientTypes.split(',').map((s) => s.trim())
        : contact.clientTypes,
    imageUrls:
      typeof contact.imageUrls === 'string'
        ? contact.imageUrls.split(',').map((s) => s.trim()).filter(Boolean)
        : contact.imageUrls || [],
    bannerImages: normalizeBannerImagesArray(contact),
    logoPicture: contact.logoPicture || '',
    callToAction: contact.callToAction || '',

    documentsRequired: parseStringArrayField(contact.documentsRequired),
    rulesAndRegulations: parseStringArrayField(contact.rulesAndRegulations),
    pricingInformation: parsePricingInformationField(contact.pricingInformation),
  };
}

function extractContactFromGetByIdResponse(data) {
  if (!data || typeof data !== 'object') return null;
  const raw = data.payload ?? data.data?.data ?? data.data;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw;
  return null;
}

const EMPTY_CONTACT_FORM = {
  title: '',
  description: '',
  infoNote: '',
  subCategoryId: [],
  contactPerson: '',
  address: '',
  houseNumber: '',
  postalCode: '',
  cityId: '',
  districtId: '',
  openingHours: '',
  email: '',
  phone: '',
  website: '',
  whatsapp: '',
  whatsappText: '',
  longitude: '',
  latitude: '',
  approvalStatus: 'PENDING',
  approvalNote: '',
  logoPicture: '',
  isActive: true,
  supportLanguages: '',
  callToAction: '',
  clientTypes: '',
  ownerName: '',
  approvedByName: '',
  comments: '',
  isBookingAppointmentEnabled: false,
  isOnlineBookingEnabled: false,
  isOfflineBookingEnabled: true,
  isChatEnabled: true,

  documentsRequired: [],
  rulesAndRegulations: [],
  pricingInformation: {
    headerTitle: '',
    headerDescription: '',
    plans: [],
  },

  pictures: [],
  picturesKeep: [],
  picturesRemove: [],
};

function getEmptyContactForm(cityId = '') {
  return { ...EMPTY_CONTACT_FORM, cityId };
}

function isPresentId(id) {
  if (id == null) return false;
  if (typeof id === 'string') return id.trim() !== '';
  return true;
}

function getServiceCategoryIdFromAuth(user, token) {
  if (user) {
    const id =
      user.serviceCategoryId ??
      user.categoryId ??
      user.contactTypeId ??
      user.contactCategoryId ??
      user.contactType;
    if (isPresentId(id)) return id;
  }
  const claims = parseJwtPayload(token);
  if (claims) {
    const id =
      claims.serviceCategoryId ??
      claims.categoryId ??
      claims.contactTypeId ??
      claims.contactType ??
      claims.ServiceCategoryId ??
      claims['service_category_id'];
    if (isPresentId(id)) return id;
  }
  return null;
}

function getCityIdFromAuth(user, token) {
  if (user) {
    const id = user.cityId ?? user.CityId;
    if (isPresentId(id)) return id;
  }
  const claims = parseJwtPayload(token);
  if (claims) {
    const id = claims.cityId ?? claims.CityId ?? claims['city_id'];
    if (isPresentId(id)) return id;
  }
  return null;
}

function getServiceCategoryIdForSubcategories(user, token, selectedContact) {
  const fromAuth = getServiceCategoryIdFromAuth(user, token);
  if (isPresentId(fromAuth)) return fromAuth;
  if (selectedContact) {
    const categoryId =
      selectedContact.serviceCategoryId ??
      selectedContact.categoryId ??
      selectedContact.contactTypeId;
    if (isPresentId(categoryId)) return categoryId;
  }
  return null;
}

function toPositiveInt(raw) {
  if (raw === undefined || raw === null || raw === '') return null;
  const n = Number.parseInt(String(raw), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function findFirstValidationError(errors) {
  return Object.values(errors).find(Boolean) || null;
}

function normalizeSubCategorySelectionValue(value) {
  if (Array.isArray(value)) {
    return value.map(Number);
  }
  if (typeof value === 'string') {
    return value.split(',').map((x) => Number(String(x).trim()));
  }
  if (value == null) return [];
  return [Number(value)];
}

const WHITESPACE_OR_AT = /[\s@]/;
const PHONE_PATTERN = /^[0-9\s()+_-]{6,20}$/;

function containsWhitespaceOrAt(str) {
  return WHITESPACE_OR_AT.test(str);
}

function isValidEmailFormat(val) {
  const at = val.indexOf('@');
  if (at <= 0 || at !== val.lastIndexOf('@')) return false;
  const local = val.slice(0, at);
  const domain = val.slice(at + 1);
  const dot = domain.lastIndexOf('.');
  if (dot <= 0 || dot >= domain.length - 1) return false;
  const domainHost = domain.slice(0, dot);
  const tld = domain.slice(dot + 1);
  if (!local || !domainHost || !tld) return false;
  return (
    !containsWhitespaceOrAt(local)
    && !containsWhitespaceOrAt(domainHost)
    && !containsWhitespaceOrAt(tld)
  );
}

function validateContactField(name, value, translate) {
  let error = '';
  const trim = (v) => String(v ?? '').trim();
  const val = trim(value);

  switch (name) {
    case 'title':
      if (!val) error = translate('pages.contacts.validation.titleRequired');
      else if (val.length > 50) error = translate('pages.contacts.validation.titleLength');
      break;
    case 'contactPerson':
      if (!val) error = translate('pages.contacts.validation.contactPersonRequired');
      else if (val.length > 50) error = translate('pages.contacts.validation.contactPersonLength');
      break;
    case 'description':
      if (!val) error = 'Description is required.';
      else if (val.length > 150) error = 'Description cannot exceed 150 characters.';
      break;
    case 'infoNote':
      if (!val) error = 'Additional Info is required.';
      else if (val.length > 150) error = 'Additional Info cannot exceed 150 characters.';
      break;
    case 'subCategoryId':
      if (!value || (Array.isArray(value) && value.length === 0)) error = 'At least one Sub Category is required.';
      break;
    case 'supportLanguages':
      if (!val) error = 'Supported Languages is required.';
      break;
    case 'address':
      if (!val) error = 'Address is required.';
      break;
    case 'houseNumber':
      if (!val) error = 'House Number is required.';
      break;
    case 'postalCode':
      if (!val) error = 'Postal Code is required.';
      else if (!/^\d{5}$/.test(val)) error = 'Postal Code must be a 5-digit number.';
      break;
    case 'districtId':
      if (!val) error = 'District is required.';
      break;
    case 'longitude':
      if (val === '') error = 'Longitude is required.';
      else {
        const lon = Number.parseFloat(val);
        if (Number.isNaN(lon) || lon < -180 || lon > 180) error = 'Longitude must be between -180 and 180.';
      }
      break;
    case 'latitude':
      if (val === '') error = 'Latitude is required.';
      else {
        const lat = Number.parseFloat(val);
        if (Number.isNaN(lat) || lat < -90 || lat > 90) error = 'Latitude must be between -90 and 90.';
      }
      break;
    case 'email':
      if (!val) error = 'Email is required.';
      else if (!isValidEmailFormat(val)) error = 'Please enter a valid email address.';
      break;
    case 'phone':
      if (!val) error = 'Phone is required.';
      else if (!PHONE_PATTERN.test(val)) error = 'Phone must be 6–20 characters.';
      break;
    case 'website':
      if (!val) error = 'Website is required.';
      else {
        const urlRe = /^(https?:\/\/)?[a-zA-Z0-9][-a-zA-Z0-9.]*\.[a-zA-Z]{2,}(\/.*)?$/i;
        if (!urlRe.test(val)) error = 'Please enter a valid website URL.';
      }
      break;
    case 'whatsapp':
      if (!val) error = 'WhatsApp is required.';
      break;
    case 'whatsappText':
      if (val.length > 100) error = 'WhatsApp message template cannot exceed 100 characters.';
      break;
    case 'callToAction':
      if (val.length > 30) error = translate('pages.contacts.validation.callToActionLength') || 'Call to Action cannot exceed 30 characters.';
      break;
    case 'openingHours':
      if (!val) error = 'Opening Hours is required.';
      break;
    default:
      break;
  }
  return error;
}

const DECLINED_APPROVAL_STATUSES = ['REJECTED', 'DECLINED', 'DECLINE'];
const DECLINED_FILTER_STATUSES = ['DECLINED', 'DECLINE'];

function isDeclinedApprovalStatus(status) {
  return DECLINED_APPROVAL_STATUSES.includes(status);
}

function contactMatchesStatusFilter(contact, statusFilter) {
  if (!statusFilter) return true;
  if (statusFilter === 'INACTIVE') return contact.isActive === false;
  if (statusFilter === 'DECLINED') return DECLINED_FILTER_STATUSES.includes(contact.approvalStatus);
  return contact.approvalStatus === statusFilter;
}

function contactMatchesSearch(contact, searchText) {
  if (!searchText) return true;
  const term = searchText.toLowerCase();
  return (
    (contact.title || '').toLowerCase().includes(term) ||
    (contact.contactPerson || '').toLowerCase().includes(term) ||
    (contact.description || '').toLowerCase().includes(term)
  );
}

function filterContactsList(contacts, statusFilter, searchText) {
  return contacts.filter(
    (contact) => contactMatchesStatusFilter(contact, statusFilter) && contactMatchesSearch(contact, searchText)
  );
}

function resolveBannerImagesForm(contactResolved) {
  if (Array.isArray(contactResolved.bannerImages)) return contactResolved.bannerImages.join(', ');
  if (typeof contactResolved.bannerImages === 'string' && contactResolved.bannerImages.trim()) {
    return contactResolved.bannerImages.trim();
  }
  if (Array.isArray(contactResolved.imageUrls)) return contactResolved.imageUrls.join(', ');
  return typeof contactResolved.imageUrls === 'string' ? contactResolved.imageUrls : '';
}

function buildFormDataFromContact(contactResolved) {
  const supportLangsForm = Array.isArray(contactResolved.supportLanguages)
    ? contactResolved.supportLanguages.join(', ')
    : (contactResolved.supportLanguages || '');
  const clientTypesForm = Array.isArray(contactResolved.clientTypes)
    ? contactResolved.clientTypes.join(', ')
    : (contactResolved.clientTypes || '');
  const initialPicturesKeep = normalizeBannerImagesArray(contactResolved);

  return {
    title: contactResolved.title || '',
    description: contactResolved.description || '',
    infoNote: contactResolved.infoNote || '',
    subCategoryId: parseSubCategoryIdsFromContact(contactResolved),
    contactPerson: contactResolved.contactPerson || '',
    address: contactResolved.address || '',
    houseNumber: contactResolved.houseNumber ?? '',
    postalCode: contactResolved.postalCode || '',
    openingHours: contactResolved.openingHours || '',
    email: contactResolved.email || '',
    phone: contactResolved.phone || '',
    website: contactResolved.website || '',
    whatsapp: contactResolved.whatsapp || '',
    whatsappText: contactResolved.whatappText ?? contactResolved.whatsappText ?? '',
    longitude: contactResolved.longitude ?? '',
    latitude: contactResolved.latitude ?? '',
    cityId: contactResolved.cityId ?? '',
    districtId: contactResolved.districtId ?? '',
    approvalStatus: contactResolved.approvalStatus || 'PENDING',
    approvalNote: contactResolved.approvalNote || '',
    logoPicture: contactResolved.logoPicture || contactResolved.profileImageUrl || '',
    isActive: contactResolved.isActive ?? true,
    supportLanguages: supportLangsForm,
    callToAction: contactResolved.callToAction || '',
    clientTypes: clientTypesForm,
    ownerName: contactResolved.ownerName || '',
    approvedByName: contactResolved.approvedByName || '',
    comments: contactResolved.comments || '',
    isBookingAppointmentEnabled: Boolean(contactResolved.isBookingAppointmentEnabled),
    isOnlineBookingEnabled: Boolean(contactResolved.isOnlineBookingEnabled),
    isOfflineBookingEnabled: resolveOfflineBookingEnabled(contactResolved.isOfflineBookingEnabled),
    isChatEnabled: contactResolved.isChatEnabled !== undefined ? Boolean(contactResolved.isChatEnabled) : true,

    documentsRequired: contactResolved.documentsRequired || [],
    rulesAndRegulations: contactResolved.rulesAndRegulations || [],
    pricingInformation: parsePricingInformationField(contactResolved.pricingInformation),

    pictures: [],
    picturesKeep: initialPicturesKeep,
    picturesRemove: [],
  };
}

function getSubcategoryNameItemsFromContact(contact) {
  if (contact?.subCategoryNames == null || contact.subCategoryNames === '') return null;
  const names = Array.isArray(contact.subCategoryNames)
    ? contact.subCategoryNames
    : String(contact.subCategoryNames)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  if (names.length === 0) return null;
  return names.map((label, i) => ({ key: `name-${contact.id}-${i}`, label }));
}

function getSubcategoryItemsFromSubCategoriesArray(contact, subs) {
  if (subs.every((x) => x && typeof x === 'object' && !Array.isArray(x))) {
    return subs
      .map((x, i) => {
        const id = Number(x.id ?? x.subCategoryId ?? x.SubCategoryId);
        const label = getSubcategoryRowLabel(x, id);
        return { key: `sub-${contact.id}-${i}`, label };
      })
      .filter((x) => x.label);
  }
  const onlyIdTokens = subs.every((x) => /^#?\d+$/.test(String(x).trim()));
  if (onlyIdTokens) return null;
  return subs
    .map((x, i) => ({
      key: `sub-${contact.id}-${i}`,
      label: String(x).trim(),
    }))
    .filter((x) => x.label);
}

function getSubcategoryDisplayItemsForContact(contact, subCategories) {
  const fromNames = getSubcategoryNameItemsFromContact(contact);
  if (fromNames) return fromNames;

  const subs = contact?.subCategories;
  if (Array.isArray(subs) && subs.length > 0) {
    const fromSubs = getSubcategoryItemsFromSubCategoriesArray(contact, subs);
    if (fromSubs) return fromSubs;
  }

  const ids = parseSubCategoryIdsFromContact(contact);
  return ids.map((id) => {
    const sc = subCategories.find(
      (s) => Number(s.id ?? s.subCategoryId ?? s.SubCategoryId) === Number(id)
    );
    const label = sc ? getSubcategoryRowLabel(sc, id) : String(id);
    return { key: `id-${contact.id}-${id}`, label };
  });
}

function validateCreateWizardStep0(formData, validateField, categoryIdResolved) {
  const newErrors = {
    title: validateField('title', formData.title),
    contactPerson: validateField('contactPerson', formData.contactPerson),
    description: validateField('description', formData.description),
    infoNote: validateField('infoNote', formData.infoNote),
    subCategoryId: validateField('subCategoryId', formData.subCategoryId),
    supportLanguages: validateField('supportLanguages', formData.supportLanguages),
    callToAction: validateField('callToAction', formData.callToAction),
  };
  if (!categoryIdResolved) {
    return {
      error: 'Your account must have a valid service category (categoryId / serviceCategoryId in profile or JWT) to save a contact.',
      errors: newErrors,
    };
  }
  return { error: findFirstValidationError(newErrors), errors: newErrors };
}

function validateCreateWizardStep1(formData, validateField) {
  const newErrors = {
    address: validateField('address', formData.address),
    houseNumber: validateField('houseNumber', formData.houseNumber),
    postalCode: validateField('postalCode', formData.postalCode),
    districtId: validateField('districtId', formData.districtId),
  };
  return { error: findFirstValidationError(newErrors), errors: newErrors };
}

function validateCreateWizardStep2(formData, validateField) {
  const newErrors = {
    email: validateField('email', formData.email),
    phone: validateField('phone', formData.phone),
    website: validateField('website', formData.website),
    whatsapp: validateField('whatsapp', formData.whatsapp),
  };
  return { error: findFirstValidationError(newErrors), errors: newErrors };
}

function validateCreateWizardStep3(formData, validateField) {
  const newErrors = {
    openingHours: validateField('openingHours', formData.openingHours),
  };
  return { error: findFirstValidationError(newErrors), errors: newErrors };
}

function collectCreateWizardStepValidation(step, formData, validateField, { bannerFiles, logoFile, categoryIdResolved }) {
  if (step === 0) return validateCreateWizardStep0(formData, validateField, categoryIdResolved);
  if (step === 1) return validateCreateWizardStep1(formData, validateField);
  if (step === 2) return validateCreateWizardStep2(formData, validateField);
  if (step === 3) return validateCreateWizardStep3(formData, validateField);
  return { error: null, errors: {} };
}

function getEditDialogSubmitLabel(isSubmitting, selectedContact, translate) {
  if (isSubmitting) {
    return selectedContact ? translate('pages.contacts.dialog.saving') : translate('pages.contacts.dialog.adding');
  }
  if (selectedContact) return translate('pages.contacts.dialog.saveChanges');
  return translate('pages.contacts.dialog.addContact');
}

function getAddressFieldHelperText(formErrors, addressLoading, formData, viewMode, addressSuggestions) {
  if (formErrors.address) return formErrors.address;
  const address = String(formData.address || '');
  if (!addressLoading && address.length >= 3 && addressSuggestions.length === 0) {
    return 'Please write correct address';
  }
  if (!viewMode && address.trim()) {
    return 'Please carefully add your address and district.';
  }
  return '';
}

function getApprovalDialogTitle(approvalStatus) {
  if (approvalStatus === 'APPROVED') return 'Approve Contact';
  if (approvalStatus === 'DECLINE') return 'Decline Contact';
  return 'Move to Pending';
}

function getApprovalDialogBodyText(approvalStatus) {
  if (approvalStatus === 'APPROVED') return 'Are you sure you want to approve this contact?';
  if (approvalStatus === 'DECLINE') return 'Please provide a reason for declining this contact:';
  return 'Are you sure you want to move this contact back to pending status?';
}

function getApprovalButtonColor(approvalStatus) {
  if (approvalStatus === 'APPROVED') return 'success';
  if (approvalStatus === 'DECLINE') return 'error';
  return 'warning';
}

function getApprovalButtonLabel(isSubmitting, approvalStatus) {
  if (isSubmitting) return 'Processing...';
  if (approvalStatus === 'APPROVED') return 'Approve';
  if (approvalStatus === 'DECLINE') return 'Decline';
  return 'Set as Pending';
}

function getLogoUploadHoverSx(isSubmitting) {
  if (isSubmitting) return {};
  return {
    borderColor: ACCENT_COLOR,
    bgcolor: 'rgba(255, 140, 66, 0.04)',
  };
}

function getLogoUploadCaption(logoFile, logoPicture, translate) {
  if (logoFile || logoPicture) {
    return translate('pages.contacts.form.changeLogo');
  }
  return translate('pages.contacts.form.addLogo');
}

function shouldShowEmptyBannerInViewMode(viewMode, picturesKeep, bannerFilesLength) {
  const len = Array.isArray(picturesKeep) ? picturesKeep.length : 0;
  return viewMode && len === 0 && bannerFilesLength === 0;
}

function LogoViewModePreview({ logoPicture, onOpenLightbox }) {
  if (!logoPicture) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Box
      component="img"
      src={logoPicture}
      alt="logo"
      onClick={() => onOpenLightbox(logoPicture)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpenLightbox(logoPicture);
        }
      }}
      role="button"
      tabIndex={0}
      sx={{
        width: 96,
        height: 96,
        objectFit: 'cover',
        borderRadius: 2,
        border: `1px solid ${GRAY_BORDER}`,
        boxShadow: IMG_SHADOW_THUMB,
        cursor: 'zoom-in',
        '&:focus-visible': { outline: `2px solid ${ACCENT_COLOR}`, outlineOffset: 2 },
      }}
    />
  );
}

function StringArrayField({ label, placeholder, items = [], onChange, disabled }) {
  const [inputValue, setInputValue] = React.useState('');
  const [editingIndex, setEditingIndex] = React.useState(null);

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed && trimmed.length <= 200) {
      if (editingIndex !== null) {
        onChange(items.map((item, index) => index === editingIndex ? trimmed : item));
        setEditingIndex(null);
      } else if (!items.includes(trimmed)) {
        onChange([...items, trimmed]);
      }
      setInputValue('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleRemove = (indexToRemove) => {
    onChange(items.filter((_, idx) => idx !== indexToRemove));
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700, color: '#29292b' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value.slice(0, 200))}
          onKeyDown={handleKeyPress}
          disabled={disabled}
          InputProps={{
            sx: { borderRadius: '8px', bgcolor: '#fff' }
          }}
          helperText={`${inputValue.length}/200`}
          FormHelperTextProps={{
            sx: { textAlign: 'right', fontWeight: 600, color: inputValue.length >= 180 ? 'error.main' : 'text.secondary', mt: 0.25 }
          }}
        />
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={disabled || !inputValue.trim()}
          aria-label={editingIndex !== null ? 'Save changes' : 'Add item'}
          title={editingIndex !== null ? 'Save changes' : 'Add item'}
          sx={{
            minWidth: 40,
            width: 40,
            height: 40,
            p: 0,
            borderRadius: '8px',
            background: '#FF8C42',
            color: '#fff',
            '&:hover': { background: '#E06526' },
            '&.Mui-disabled': { background: 'rgba(0, 0, 0, 0.12)' }
          }}
        >
          {editingIndex !== null ? <CheckCircle sx={{ fontSize: 20 }} /> : <Add sx={{ fontSize: 20 }} />}
        </Button>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 150, overflowY: 'auto', pr: 0.5 }}>
        {items.map((item, index) => (
          <Paper
            key={`${item}-${index}`}
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: '6px 12px',
              borderRadius: '8px',
              border: '1px solid #e0e0e0',
              bgcolor: '#fafafa',
            }}
          >
            <Typography variant="body2" sx={{ color: '#37373a', pr: 1, wordBreak: 'break-word', fontSize: '0.85rem', flex: 1 }}>
              {item}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <Tooltip title="Edit">
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Edit ${item}`}
                    onClick={() => {
                      setInputValue(item);
                      setEditingIndex(index);
                    }}
                    disabled={disabled}
                    sx={{ color: '#64748b', p: 0.5, '&:hover': { bgcolor: 'rgba(100, 116, 139, 0.08)' } }}
                  >
                    <Edit sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton
                size="small"
                aria-label={`Delete ${item}`}
                onClick={() => {
                  handleRemove(index);
                  if (editingIndex === index) {
                    setEditingIndex(null);
                    setInputValue('');
                  }
                }}
                disabled={disabled}
                sx={{ color: '#ef4444', p: 0.5, '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.08)' } }}
              >
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Paper>
        ))}

      </Box>
    </Box>
  );
}

const BADGE_PRESETS = [
  { value: '', label: 'No badge', bgcolor: '#f1f5f9', color: '#64748b', border: '#e2e8f0' },
  { value: 'Most Popular', label: 'Most Popular', bgcolor: '#ffedd5', color: '#9a3412', border: '#fed7aa' },
  { value: 'Flexible', label: 'Flexible', bgcolor: '#e0f2fe', color: '#0369a1', border: '#bae6fd' },
  { value: 'Best Value', label: 'Best Value', bgcolor: '#f3e8ff', color: '#6b21a8', border: '#e9d5ff' },
  { value: 'Recommended', label: 'Recommended', bgcolor: '#dcfce7', color: '#166534', border: '#bbf7d0' },
  { value: 'Limited Time', label: 'Limited Time', bgcolor: '#fee2e2', color: '#991b1b', border: '#fecaca' },
];

const PERIOD_PRESETS = [
  { value: '/ month', labelKey: 'pages.contacts.form.periodMonth', fallback: 'Monthly' },
  { value: '/ year', labelKey: 'pages.contacts.form.periodYear', fallback: 'Yearly' },
  { value: '/ week', labelKey: 'pages.contacts.form.periodWeek', fallback: 'Weekly' },
  { value: '/ day', labelKey: 'pages.contacts.form.periodDay', fallback: 'Daily' },
  { value: '/ one-time', labelKey: 'pages.contacts.form.periodOneTime', fallback: 'One-time' },
  { value: '/ session', labelKey: 'pages.contacts.form.periodSession', fallback: 'Per session' },
  { value: '/ consultation', labelKey: 'pages.contacts.form.periodConsultation', fallback: 'Per consultation' },
  { value: 'Free', labelKey: 'pages.contacts.form.periodFree', fallback: 'Free' },
];

function getPeriodLabel(period, t) {
  if (!period) return '';
  const match = PERIOD_PRESETS.find((p) => p.value === period);
  if (match) return (t ? t(match.labelKey) : '') || match.fallback;
  if (period === 'Monthly' || period === 'Monatlich') return (t ? t('pages.contacts.form.periodMonth') : '') || 'Monthly';
  if (period === 'Yearly' || period === 'Jährlich') return (t ? t('pages.contacts.form.periodYear') : '') || 'Yearly';
  if (period === 'Wöchentlich' || period === 'Wöchentlich') return (t ? t('pages.contacts.form.periodWeek') : '') || 'Weekly';
  if (period === 'Daily' || period === 'Täglich') return (t ? t('pages.contacts.form.periodDay') : '') || 'Daily';
  if (period === 'One-time' || period === 'Einmalig') return (t ? t('pages.contacts.form.periodOneTime') : '') || 'One-time';
  return period;
}

function PricingHighlightsEditor({ highlights = [], onChange, disabled, t }) {
  const [newHighlight, setNewHighlight] = useState('');

  const handleAdd = () => {
    const trimmed = newHighlight.trim();
    if (!trimmed) return;
    if (!highlights.includes(trimmed)) {
      onChange([...highlights, trimmed]);
    }
    setNewHighlight('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleRemove = (indexToRemove) => {
    onChange(highlights.filter((_, idx) => idx !== indexToRemove));
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: '12px',
        bgcolor: '#f8fafc',
        border: '1px solid #e2e8f0',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle sx={{ fontSize: 16, color: '#16a34a' }} />
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 750, color: '#1e293b' }}>
            {t('pages.contacts.form.pricingHighlights') || 'Highlights & Key Benefits'}
          </Typography>
        </Box>
        <Chip
          label={`${highlights.length} ${highlights.length === 1 ? 'item' : 'items'}`}
          size="small"
          sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#e2e8f0', color: '#475569' }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t('pages.contacts.form.pricingHighlightPlaceholder') || 'Add a plan highlight (e.g. 100% Visa Approved by Embassy)...'}
          value={newHighlight}
          onChange={(e) => setNewHighlight(e.target.value.slice(0, 50))}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          inputProps={{ maxLength: 50 }}
          helperText={`${newHighlight.length}/50`}
          FormHelperTextProps={{
            sx: { textAlign: 'right', fontWeight: 600, color: newHighlight.length >= 45 ? 'error.main' : 'text.secondary', mt: 0.25 },
          }}
          InputProps={{
            sx: { borderRadius: '8px', bgcolor: '#ffffff', fontSize: '0.84rem' },
          }}
        />
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={disabled || !newHighlight.trim()}
          sx={{
            height: 40,
            minHeight: 40,
            maxHeight: 40,
            flexShrink: 0,
            borderRadius: '8px',
            px: 2.2,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.82rem',
            bgcolor: '#063937',
            color: '#fff',
            boxShadow: 'none',
            '&:hover': { bgcolor: '#042725', boxShadow: 'none' },
          }}
        >
          Add
        </Button>
      </Box>

      {highlights.length === 0 ? (
        <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic', display: 'block' }}>
          No highlights added yet. Add at least 1-4 key benefits to help customers compare your offer.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {highlights.map((item, idx) => (
            <Paper
              key={`${item}-${idx}`}
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                py: 0.75,
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                <CheckCircle sx={{ fontSize: 14, color: '#16a34a', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.8rem', color: '#1e293b', fontWeight: 500, overflowWrap: 'anywhere' }}>
                  {item}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => handleRemove(idx)}
                disabled={disabled}
                sx={{
                  color: '#94a3b8',
                  p: 0.5,
                  '&:hover': { color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.08)' },
                }}
              >
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}

function PricingInformationField({ label, items, value, onChange, disabled, t }) {
  const [openPlans, setOpenPlans] = useState({});

  const pricingData = useMemo(() => {
    return parsePricingInformationField(value !== undefined ? value : items);
  }, [value, items]);

  const { headerTitle = '', headerDescription = '', plans = [] } = pricingData;

  const updateHeaderField = (field, val) => {
    onChange({
      ...pricingData,
      [field]: val,
    });
  };

  const updatePlans = (newPlans) => {
    onChange({
      ...pricingData,
      plans: newPlans,
    });
  };

  const togglePlanCollapse = (index) => {
    setOpenPlans((prev) => ({
      ...prev,
      [index]: prev[index] !== undefined ? !prev[index] : false,
    }));
  };

  const updatePlan = (index, field, val) => {
    updatePlans(plans.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [field]: val } : item
    )));
  };

  const addPlan = () => {
    const newIndex = plans.length;
    updatePlans([
      ...plans,
      {
        title: '',
        badge: '',
        description: '',
        price: '',
        period: '/ month',
        hint: '',
        highlights: [],
      },
    ]);
    setOpenPlans((prev) => ({ ...prev, [newIndex]: true }));
  };

  const removePlan = (index) => {
    updatePlans(plans.filter((_, idx) => idx !== index));
  };

  return (
    <Box sx={{ mb: 2.5 }}>
      {/* Header bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '8px',
                bgcolor: '#fff3eb',
                color: ACCENT_COLOR,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <TicketIcon sx={{ fontSize: 16 }} />
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '0.95rem' }}>
              {label || t('pages.contacts.form.pricingInformation') || 'Pricing / Fees Information'}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#64748b' }}>
            {t('pages.contacts.form.pricingInformationDescription') || 'Configure transparent pricing tiers, billing intervals, promotional badges, and plan highlights.'}
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={addPlan}
          disabled={disabled}
          sx={{
            borderRadius: '8px',
            px: 2,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.82rem',
            bgcolor: ACCENT_COLOR,
            color: '#ffffff',
            boxShadow: 'none',
            '&:hover': { bgcolor: '#E06526', boxShadow: 'none' },
          }}
        >
          {t('pages.contacts.form.addPricingPlan') || 'Add Pricing Plan'}
        </Button>
      </Box>

      {/* Section Header Inputs: headerTitle & headerDescription */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 2.5,
          borderRadius: '14px',
          bgcolor: '#f8fafc',
          border: '1.5px solid #e2e8f0',
        }}
      >
        <Typography sx={{ fontSize: '0.84rem', fontWeight: 750, color: '#0f172a', mb: 1.5 }}>
          {t('pages.contacts.form.pricingHeaderSection') || 'Package Information'}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label={t('pages.contacts.form.pricingHeaderTitle') || 'Package Title'}
              placeholder={t('pages.contacts.form.pricingHeaderTitlePlaceholder') || 'e.g. Insurance Packages & What You Get'}
              value={headerTitle}
              onChange={(e) => updateHeaderField('headerTitle', e.target.value.slice(0, 50))}
              disabled={disabled}
              inputProps={{ maxLength: 50 }}
              InputProps={{ sx: { borderRadius: '8px', bgcolor: '#ffffff' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label={t('pages.contacts.form.pricingHeaderDescription') || 'Package Description'}
              placeholder={t('pages.contacts.form.pricingHeaderDescriptionPlaceholder') || 'e.g. Official visa-compliant packages for Germany'}
              value={headerDescription}
              onChange={(e) => updateHeaderField('headerDescription', e.target.value.slice(0, 150))}
              disabled={disabled}
              inputProps={{ maxLength: 150 }}
              helperText={`${headerDescription.length}/150`}
              FormHelperTextProps={{
                sx: { textAlign: 'right', fontWeight: 600, color: headerDescription.length >= 140 ? 'error.main' : 'text.secondary', mt: 0.25 },
              }}
              InputProps={{ sx: { borderRadius: '8px', bgcolor: '#ffffff' } }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Empty State vs Plans List */}
      {plans.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: '16px',
            textAlign: 'center',
            borderStyle: 'dashed',
            borderColor: '#cbd5e1',
            bgcolor: '#fafbff',
          }}
        >
          <Box
            sx={{
              width: 50,
              height: 50,
              mx: 'auto',
              mb: 1.5,
              display: 'grid',
              placeItems: 'center',
              borderRadius: '14px',
              bgcolor: '#fff3eb',
              color: ACCENT_COLOR,
            }}
          >
            <TicketIcon sx={{ fontSize: 26 }} />
          </Box>
          <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1rem', mb: 0.5 }}>
            {t('pages.contacts.form.pricingEmptyTitle') || 'No pricing plans added yet'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', maxWidth: 480, mx: 'auto', mb: 2.5 }}>
            {t('pages.contacts.form.pricingEmptyDescription') || 'Add pricing plans with prices, billing cycles, and feature checklists so customers can easily choose the right plan.'}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={addPlan}
              disabled={disabled}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 700,
                bgcolor: ACCENT_COLOR,
                color: '#fff',
                px: 2.5,
                py: 1,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#E06526', boxShadow: 'none' },
              }}
            >
              {t('pages.contacts.form.addPricingPlan') || 'Add Pricing Plan'}
            </Button>
          </Box>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {plans.map((item, index) => {
            const isPopular = item.badge === 'Most Popular';
            const isFlexible = item.badge === 'Flexible';
            const isPlanOpen = openPlans[index] !== false;

            return (
              <Paper
                key={`pricing-${index}`}
                variant="outlined"
                sx={{
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: '1.5px solid',
                  borderColor: isPopular ? '#fed7aa' : isFlexible ? '#bae6fd' : '#e2e8f0',
                  bgcolor: '#ffffff',
                  boxShadow: isPopular
                    ? '0 6px 20px rgba(254, 215, 170, 0.25)'
                    : '0 2px 10px rgba(15, 23, 42, 0.035)',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                }}
              >
                {/* Card Top Header (Click to Compress / Expand) */}
                <Box
                  onClick={() => togglePlanCollapse(index)}
                  sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    cursor: 'pointer',
                    userSelect: 'none',
                    bgcolor: isPopular ? '#fffaf6' : isFlexible ? '#f0f9ff' : '#f8fafc',
                    borderBottom: isPlanOpen ? '1px solid' : 'none',
                    borderColor: isPopular ? '#ffedd5' : isFlexible ? '#e0f2fe' : '#eef2f6',
                    '&:hover': {
                      bgcolor: isPopular ? '#fff7ed' : isFlexible ? '#e0f2fe' : '#f1f5f9',
                    },
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
                    <Chip
                      size="small"
                      label={`PLAN ${String(index + 1).padStart(2, '0')}`}
                      sx={{
                        fontWeight: 800,
                        fontSize: '0.68rem',
                        letterSpacing: '0.5px',
                        bgcolor: isPopular ? '#ffedd5' : isFlexible ? '#e0f2fe' : '#e2e8f0',
                        color: isPopular ? '#9a3412' : isFlexible ? '#0369a1' : '#475569',
                        borderRadius: '6px',
                        height: 22,
                      }}
                    />
                    <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.92rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.title || t('pages.contacts.form.untitledPricingPlan') || 'Untitled Plan'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={t('pages.contacts.form.removePricingPlan') || 'Remove plan'}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          removePlan(index);
                        }}
                        disabled={disabled}
                        sx={{
                          color: '#94a3b8',
                          '&:hover': { color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.08)' },
                        }}
                      >
                        <Delete sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={() => togglePlanCollapse(index)}
                      sx={{ color: '#64748b', p: 0.5 }}
                    >
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 22,
                          transform: isPlanOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.25s ease',
                        }}
                      />
                    </IconButton>
                  </Box>
                </Box>

                {/* Card Form Body wrapped in Collapse */}
                <Collapse in={isPlanOpen} timeout="auto" unmountOnExit={false}>
                  <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Grid container spacing={2}>
                      {/* Plan Title */}
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('pages.contacts.form.pricingTitle') || 'Plan Title'}
                          placeholder="e.g. Student Health Insurance"
                          value={item.title || ''}
                          onChange={(e) => updatePlan(index, 'title', e.target.value.slice(0, 50))}
                          disabled={disabled}
                          inputProps={{ maxLength: 50 }}
                          InputProps={{ sx: { borderRadius: '8px' } }}
                        />
                      </Grid>

                      {/* Badge Dropdown */}
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small" disabled={disabled}>
                          <InputLabel>{t('pages.contacts.form.pricingBadge') || 'Badge'}</InputLabel>
                          <Select
                            label={t('pages.contacts.form.pricingBadge') || 'Badge'}
                            value={item.badge || ''}
                            onChange={(e) => updatePlan(index, 'badge', e.target.value)}
                            sx={{ borderRadius: '8px' }}
                            renderValue={(selected) => {
                              if (!selected) {
                                return <Typography sx={{ color: '#94a3b8', fontSize: '0.84rem' }}>No badge</Typography>;
                              }
                              const matched = BADGE_PRESETS.find((b) => b.value === selected);
                              return (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Chip
                                    size="small"
                                    label={selected}
                                    sx={{
                                      height: 22,
                                      fontSize: '0.7rem',
                                      fontWeight: 750,
                                      bgcolor: matched?.bgcolor || '#f1f5f9',
                                      color: matched?.color || '#334155',
                                      border: matched?.border ? `1px solid ${matched.border}` : '1px solid #e2e8f0',
                                      borderRadius: '6px',
                                    }}
                                  />
                                </Box>
                              );
                            }}
                          >
                            {BADGE_PRESETS.map((b) => (
                              <MenuItem key={b.value || 'none'} value={b.value}>
                                {b.value ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                      size="small"
                                      label={b.label}
                                      sx={{
                                        fontWeight: 750,
                                        bgcolor: b.bgcolor,
                                        color: b.color,
                                        border: `1px solid ${b.border}`,
                                        borderRadius: '6px',
                                        height: 22,
                                        fontSize: '0.72rem',
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <Typography sx={{ color: '#64748b', fontSize: '0.84rem' }}>
                                    {t('pages.contacts.form.noPricingBadge') || 'No badge'}
                                  </Typography>
                                )}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Price */}
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('pages.contacts.form.pricingPrice') || 'Price'}
                          placeholder="e.g. 1.25"
                          value={item.price || ''}
                          onChange={(e) => updatePlan(index, 'price', e.target.value.slice(0, 50))}
                          disabled={disabled}
                          type="number"
                          error={String(item.price || '').trim() !== '' && (!Number.isFinite(Number(item.price)) || Number(item.price) < 0)}
                          helperText={String(item.price || '').trim() !== '' && (!Number.isFinite(Number(item.price)) || Number(item.price) < 0) ? 'Enter a valid non-negative price, or leave it blank.' : ''}
                          inputProps={{ maxLength: 50, min: 0, step: 'any' }}
                          InputProps={{ sx: { borderRadius: '8px' } }}
                        />
                      </Grid>

                      {/* Billing Period Dropdown */}
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small" disabled={disabled}>
                          <InputLabel>{t('pages.contacts.form.pricingPeriod') || 'Billing Period'}</InputLabel>
                          <Select
                            label={t('pages.contacts.form.pricingPeriod') || 'Billing Period'}
                            value={item.period || '/ month'}
                            onChange={(e) => updatePlan(index, 'period', e.target.value)}
                            sx={{ borderRadius: '8px' }}
                            renderValue={(selected) => (
                              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a' }}>
                                {getPeriodLabel(selected, t)}
                              </Typography>
                            )}
                          >
                            {PERIOD_PRESETS.map((p) => (
                              <MenuItem key={p.value} value={p.value}>
                                <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#1e293b' }}>
                                  {t(p.labelKey) || p.fallback}
                                </Typography>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Price Hint / Subtitle */}
                      <Grid item xs={12} sm={12} md={8}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('pages.contacts.form.pricingHint') || 'Price Hint / Subtitle'}
                          placeholder={'e.g. Starts from \u20AC125/mo \u2022 Free incoming travel insurance included'}
                          value={item.hint || ''}
                          onChange={(e) => updatePlan(index, 'hint', e.target.value.slice(0, 100))}
                          disabled={disabled}
                          inputProps={{ maxLength: 100 }}
                          helperText={`${(item.hint || '').length}/100`}
                          FormHelperTextProps={{
                            sx: { textAlign: 'right', fontWeight: 600, color: (item.hint || '').length >= 90 ? 'error.main' : 'text.secondary', mt: 0.25 },
                          }}
                          InputProps={{
                            sx: { borderRadius: '8px' },
                            startAdornment: (
                              <InputAdornment position="start">
                                <InfoIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      {/* Description */}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          minRows={2}
                          label={t('pages.contacts.form.pricingDescription') || 'Plan Description'}
                          placeholder="e.g. Statutory & expat health cover fully compliant with German student visa requirements & university enrolment."
                          value={item.description || ''}
                          onChange={(e) => updatePlan(index, 'description', e.target.value.slice(0, 500))}
                          disabled={disabled}
                          inputProps={{ maxLength: 500 }}
                          helperText={`${(item.description || '').length}/500`}
                          FormHelperTextProps={{
                            sx: { textAlign: 'right', fontWeight: 600, color: (item.description || '').length >= 480 ? 'error.main' : 'text.secondary', mt: 0.25 },
                          }}
                          InputProps={{ sx: { borderRadius: '8px' } }}
                        />
                      </Grid>

                      {/* Highlights & Benefits */}
                      <Grid item xs={12}>
                        <PricingHighlightsEditor
                          highlights={Array.isArray(item.highlights) ? item.highlights : []}
                          onChange={(highlights) => updatePlan(index, 'highlights', highlights)}
                          disabled={disabled}
                          t={t}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
const DAYS_CONFIG = [
  { key: 'Mon', fullLabel: 'Monday', shortLabel: 'Mon', dayOfWeek: 1 },
  { key: 'Tue', fullLabel: 'Tuesday', shortLabel: 'Tue', dayOfWeek: 2 },
  { key: 'Wed', fullLabel: 'Wednesday', shortLabel: 'Wed', dayOfWeek: 3 },
  { key: 'Thu', fullLabel: 'Thursday', shortLabel: 'Thu', dayOfWeek: 4 },
  { key: 'Fri', fullLabel: 'Friday', shortLabel: 'Fri', dayOfWeek: 5 },
  { key: 'Sat', fullLabel: 'Saturday', shortLabel: 'Sat', dayOfWeek: 6 },
  { key: 'Sun', fullLabel: 'Sunday', shortLabel: 'Sun', dayOfWeek: 7 },
];

function formatTimeTo12Hour(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return '';
  const trimmed = timeStr.trim();
  if (/am|pm/i.test(trimmed)) return trimmed;

  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12;
  if (hours === 0) hours = 12;

  const formattedHours = String(hours).padStart(2, '0');
  return `${formattedHours}:${minutes} ${period}`;
}

function convert12To24Hour(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return '09:00';
  const trimmed = timeStr.trim();

  if (/^\d{1,2}:\d{2}$/.test(trimmed)) {
    const [h, m] = trimmed.split(':');
    return `${h.padStart(2, '0')}:${m}`;
  }

  const match = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!match) return '09:00';

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = (match[3] || '').toUpperCase();

  if (period === 'PM' && hours < 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;

  return `${String(hours).padStart(2, '0')}:${minutes}`;
}

function parseScheduleFromOpeningHours(rawInput) {
  const initial = {
    Mon: { open: true, sessions: [{ open: '09:00', close: '18:00' }] },
    Tue: { open: true, sessions: [{ open: '09:00', close: '18:00' }] },
    Wed: { open: true, sessions: [{ open: '09:00', close: '18:00' }] },
    Thu: { open: true, sessions: [{ open: '09:00', close: '18:00' }] },
    Fri: { open: true, sessions: [{ open: '09:00', close: '18:00' }] },
    Sat: { open: false, sessions: [{ open: '09:00', close: '18:00' }] },
    Sun: { open: false, sessions: [{ open: '09:00', close: '18:00' }] },
  };

  if (!rawInput) return initial;

  let parsed = rawInput;
  if (typeof rawInput === 'string') {
    const trimmed = rawInput.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        parsed = JSON.parse(rawInput);
      } catch { }
    }
  }

  if (parsed && typeof parsed === 'object') {
    if (Array.isArray(parsed.days)) {
      DAYS_CONFIG.forEach(({ key, fullLabel, dayOfWeek }) => {
        const found = parsed.days.find((d) => d.day === fullLabel || d.dayOfWeek === dayOfWeek);
        if (found) {
          const isClosed = Boolean(found.isClosed);
          const sessions = Array.isArray(found.sessions) && found.sessions.length > 0
            ? found.sessions.map((s) => ({
              open: convert12To24Hour(s.open || s.start || '09:00'),
              close: convert12To24Hour(s.close || s.end || '18:00'),
            }))
            : [{ open: '09:00', close: '18:00' }];
          initial[key] = {
            open: !isClosed,
            sessions,
          };
        }
      });
      return initial;
    }
  }

  // Text format fallback parsing e.g. "Mon: 09:00 AM - 12:00 PM, 01:00 PM - 05:00 PM; Tue: Closed"
  if (typeof rawInput === 'string') {
    const dayEntries = rawInput.trim().split(/[;\n]+/);
    dayEntries.forEach((entry) => {
      const trimmed = entry.trim();
      if (!trimmed) return;
      const match = trimmed.match(/^(Mon|Tue|Wed|Thu|Fri|Sat|Sun|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s*:\s*(.*)$/i);
      if (match) {
        const dayKey = DAYS_CONFIG.find((d) => d.key.toLowerCase() === match[1].toLowerCase() || d.fullLabel.toLowerCase() === match[1].toLowerCase())?.key;
        const val = match[2].trim();
        if (dayKey) {
          if (/closed|off|zu|geschlossen/i.test(val)) {
            initial[dayKey] = { open: false, sessions: [{ open: '09:00', close: '18:00' }] };
          } else {
            const timesMatches = Array.from(val.matchAll(/(\d{1,2}:\d{2}(?:\s*[AP]M)?)\s*[-–—\sto]+\s*(\d{1,2}:\d{2}(?:\s*[AP]M)?)/gi));
            if (timesMatches.length > 0) {
              const extractedSessions = timesMatches.map((m) => ({
                open: convert12To24Hour(m[1]),
                close: convert12To24Hour(m[2]),
              }));
              initial[dayKey] = { open: true, sessions: extractedSessions };
            }
          }
        }
      }
    });
  }

  return initial;
}

function serializeScheduleToOpeningHours(schedule) {
  const payload = {
    days: DAYS_CONFIG.map(({ key, fullLabel, dayOfWeek }) => {
      const item = schedule[key] || { open: false, sessions: [{ open: '09:00', close: '18:00' }] };
      const isClosed = !item.open || !item.sessions || item.sessions.length === 0;
      return {
        day: fullLabel,
        dayOfWeek,
        isClosed,
        sessions: isClosed
          ? []
          : item.sessions.map((s) => ({
            open: s.open || '09:00',
            close: s.close || '18:00',
          })),
      };
    }),
  };
  return JSON.stringify(payload);
}

const getTranslatedDayLabel = (fullLabel, t) => {
  const keyMap = {
    Monday: 'common.days.monday',
    Tuesday: 'common.days.tuesday',
    Wednesday: 'common.days.wednesday',
    Thursday: 'common.days.thursday',
    Friday: 'common.days.friday',
    Saturday: 'common.days.saturday',
    Sunday: 'common.days.sunday',
  };
  const key = keyMap[fullLabel];
  if (key && t) {
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }
  return fullLabel;
};

function BusinessHoursScheduleField({ value, onChange, disabled, error, helperText }) {
  const { t } = useLanguage();
  const [schedule, setSchedule] = React.useState(() => parseScheduleFromOpeningHours(value));

  React.useEffect(() => {
    setSchedule(parseScheduleFromOpeningHours(value));
  }, [value]);

  const updateSchedule = (newSchedule) => {
    setSchedule(newSchedule);
    const serialized = serializeScheduleToOpeningHours(newSchedule);
    onChange({ target: { name: 'openingHours', value: serialized } });
  };

  const handleToggleDay = (key, open) => {
    const cur = schedule[key] || { open: false, sessions: [{ open: '09:00', close: '18:00' }] };
    const sess = cur.sessions && cur.sessions.length > 0 ? cur.sessions : [{ open: '09:00', close: '18:00' }];
    const next = { ...schedule, [key]: { ...cur, open, sessions: sess } };
    updateSchedule(next);
  };

  const handleSessionTimeChange = (key, sIdx, field, timeVal) => {
    const cur = schedule[key];
    const newSessions = [...cur.sessions];
    newSessions[sIdx] = { ...newSessions[sIdx], [field]: timeVal };
    const next = { ...schedule, [key]: { ...cur, sessions: newSessions } };
    updateSchedule(next);
  };

  const handleAddSession = (key) => {
    const cur = schedule[key] || { open: true, sessions: [] };
    if (cur.sessions && cur.sessions.length >= 2) return;
    const lastSess = cur.sessions[cur.sessions.length - 1];
    let newOpen = '13:00';
    let newClose = '17:00';
    if (lastSess && lastSess.close) {
      const [h, m] = lastSess.close.split(':').map(Number);
      if (!isNaN(h)) {
        const startH = Math.min(h + 1, 22);
        const endH = Math.min(startH + 4, 23);
        newOpen = `${String(startH).padStart(2, '0')}:${String(m || 0).padStart(2, '0')}`;
        newClose = `${String(endH).padStart(2, '0')}:${String(m || 0).padStart(2, '0')}`;
      }
    }
    const newSess = { open: newOpen, close: newClose };
    const next = { ...schedule, [key]: { ...cur, open: true, sessions: [...(cur.sessions || []), newSess] } };
    updateSchedule(next);
  };

  const handleRemoveSession = (key, sIdx) => {
    const cur = schedule[key];
    const newSessions = cur.sessions.filter((_, idx) => idx !== sIdx);
    const isOpen = newSessions.length > 0;
    const next = {
      ...schedule,
      [key]: {
        ...cur,
        open: isOpen,
        sessions: newSessions.length > 0 ? newSessions : [{ open: '09:00', close: '18:00' }],
      },
    };
    updateSchedule(next);
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#29292b', display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <AccessTime sx={{ color: '#FF8C42', fontSize: 18 }} />
        {t('pages.contacts.form.businessHours') || 'Business Hours'}
        <Box component="span" sx={{ color: '#d32f2f', fontWeight: 700, ml: 0.25 }}>*</Box>
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: `1px solid ${error ? '#DC2626' : '#E2E8F0'}`,
          borderRadius: '12px',
          bgcolor: '#FFFFFF',
          boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {DAYS_CONFIG.map(({ key, fullLabel }) => {
            const item = schedule[key] || { open: false, sessions: [{ open: '09:00', close: '18:00' }] };
            return (
              <Box
                key={key}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  px: 1.5,
                  py: 1,
                  borderRadius: '8px',
                  bgcolor: item.open ? '#FAFAFA' : '#F8FAFC',
                  border: '1px solid',
                  borderColor: item.open ? '#E2E8F0' : '#EDF2F7',
                  transition: 'all 0.15s ease',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 32 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={item.open}
                        onChange={(e) => handleToggleDay(key, e.target.checked)}
                        disabled={disabled}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#FF8C42',
                            '& + .MuiSwitch-track': {
                              backgroundColor: '#FF8C42',
                            },
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 600, color: item.open ? '#1E293B' : '#94A3B8', fontSize: '0.875rem' }}>
                        {getTranslatedDayLabel(fullLabel, t)}
                      </Typography>
                    }
                    sx={{ ml: -0.5, mr: 0 }}
                  />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-end' }}>
                  {item.open ? (
                    <>
                      {item.sessions.map((sess, sIdx) => (
                        <Box key={sIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <TextField
                            type="time"
                            size="small"
                            value={sess.open}
                            onChange={(e) => handleSessionTimeChange(key, sIdx, 'open', e.target.value)}
                            disabled={disabled}
                            inputProps={{ step: 1800 }}
                            sx={{
                              width: 105,
                              '& .MuiOutlinedInput-root': {
                                height: 32,
                                borderRadius: '6px',
                                fontSize: '0.825rem',
                                bgcolor: '#FFFFFF',
                                '& fieldset': { borderColor: '#CBD5E1' },
                                '&:hover fieldset': { borderColor: '#FF8C42' },
                                '&.Mui-focused fieldset': { borderColor: '#FF8C42' },
                              },
                            }}
                          />
                          <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, px: 0.25 }}>
                            –
                          </Typography>
                          <TextField
                            type="time"
                            size="small"
                            value={sess.close}
                            onChange={(e) => handleSessionTimeChange(key, sIdx, 'close', e.target.value)}
                            disabled={disabled}
                            inputProps={{ step: 1800 }}
                            sx={{
                              width: 105,
                              '& .MuiOutlinedInput-root': {
                                height: 32,
                                borderRadius: '6px',
                                fontSize: '0.825rem',
                                bgcolor: '#FFFFFF',
                                '& fieldset': { borderColor: '#CBD5E1' },
                                '&:hover fieldset': { borderColor: '#FF8C42' },
                                '&.Mui-focused fieldset': { borderColor: '#FF8C42' },
                              },
                            }}
                          />
                          <Typography variant="caption" sx={{ color: '#0F172A', fontWeight: 700, fontSize: '0.78rem', ml: 0.5, bgcolor: '#F1F5F9', px: 1, py: 0.4, borderRadius: '6px', minWidth: 145, textAlign: 'center' }}>
                            {`${formatTimeTo12Hour(sess.open)} – ${formatTimeTo12Hour(sess.close)}`}
                          </Typography>
                          {item.sessions.length > 1 && !disabled && (
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveSession(key, sIdx)}
                              sx={{ color: '#EF4444', p: 0.4, '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' } }}
                              title={t('pages.contacts.form.removeSession') || 'Remove session'}
                            >
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                        </Box>
                      ))}
                      {!disabled && item.sessions.length < 2 && (
                        <Button
                          size="small"
                          onClick={() => handleAddSession(key)}
                          startIcon={<Add sx={{ fontSize: 14 }} />}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: '#FF8C42',
                            py: 0.1,
                            px: 0.75,
                            minWidth: 'auto',
                            '&:hover': { bgcolor: 'rgba(255,140,66,0.08)' },
                          }}
                        >
                          {t('pages.contacts.form.addSession') || 'Add session'}
                        </Button>
                      )}
                    </>
                  ) : (
                    <Box sx={{ minHeight: 32, display: 'flex', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, px: 1, py: 0.4, bgcolor: '#F1F5F9', borderRadius: '6px' }}>
                        {t('pages.contacts.form.closed') || 'Closed'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {error && (
          <FormHelperText error sx={{ mt: 1, fontWeight: 600 }}>
            {helperText}
          </FormHelperText>
        )}
      </Paper>
    </Box>
  );
}

function trimFormValue(v) {
  return String(v ?? '').trim();
}

function arrayToStringField(value) {
  if (!value) return '';
  if (typeof value === 'string') return value.trim() ? value : '';
  if (Array.isArray(value)) return value.join(',');
  return '';
}

function parseClientTypesToArray(v) {
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  if (typeof v === 'string') return v.split(',').map((s) => s.trim()).filter(Boolean);
  return [];
}

function parseCoordinateField(value) {
  if (value == null || value === '') return null;
  return Number.parseFloat(value);
}

function normalizeHouseNumber(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed || null;
}

function buildSupportLanguagesString(formData) {
  if (Array.isArray(formData.supportLanguages)) {
    return formData.supportLanguages.join(', ');
  }
  if (typeof formData.supportLanguages === 'string') {
    return formData.supportLanguages.trim();
  }
  return arrayToStringField(formData.supportLanguages);
}

function buildContactSubmitPayload(formData, bannerFiles, logoFile, categoryIdResolved) {
  const s = trimFormValue;
  const existingBannerRefs = Array.isArray(formData.picturesKeep) ? formData.picturesKeep : [];
  const newBannerFiles = bannerFiles.map((f) => f.name);
  const subCategoryIdNums = (Array.isArray(formData.subCategoryId) ? formData.subCategoryId : [])
    .map(Number)
    .filter((n) => Number.isFinite(n));

  const payload = {
    title: formData.title,
    infoNote: formData.infoNote || '',
    description: formData.description || '',
    supportLanguages: buildSupportLanguagesString(formData),
    callToAction: formData.callToAction ? formData.callToAction.trim() : '',
    clientTypes: parseClientTypesToArray(formData.clientTypes),
    subCategoryId: subCategoryIdNums,
    subCategoryIds: subCategoryIdNums,
    contactPerson: formData.contactPerson || '',
    address: formData.address,
    houseNumber: normalizeHouseNumber(formData.houseNumber),
    postalCode: formData.postalCode || '',
    email: formData.email || '',
    phone: formData.phone || '',
    website: formData.website || '',
    whatsapp: formData.whatsapp || '',
    longitude: parseCoordinateField(formData.longitude),
    latitude: parseCoordinateField(formData.latitude),
    isActive: formData.isActive ?? true,
    isBookingAppointmentEnabled: Boolean(formData.isBookingAppointmentEnabled),
    isOnlineBookingEnabled: Boolean(formData.isOnlineBookingEnabled),
    isOfflineBookingEnabled: Boolean(formData.isOfflineBookingEnabled),
    isChatEnabled: Boolean(formData.isChatEnabled),
    pictures: bannerFiles.length > 0 ? newBannerFiles : (Array.isArray(formData.pictures) ? formData.pictures : []),
    picturesKeep: existingBannerRefs,
    picturesRemove: Array.isArray(formData.picturesRemove) ? formData.picturesRemove : [],
    logoPicture: logoFile ? logoFile.name : s(formData.logoPicture),
    cityId: toPositiveInt(formData.cityId),
    districtId: toPositiveInt(formData.districtId),
    categoryId: categoryIdResolved,
    openingHours: formData.openingHours
      ? (typeof formData.openingHours === 'string' && (formData.openingHours.startsWith('{') || formData.openingHours.startsWith('['))
        ? JSON.parse(formData.openingHours)
        : formData.openingHours)
      : null,
    whatappText: formData.whatsappText || '',
    approvalStatus: formData.approvalStatus || 'PENDING',
    approvalNote: formData.approvalNote || '',
    imageUrlArray: null,
    contactRating: null,
    subCategories: subCategoryIdNums,
    isDistanceAvailable: false,
    rating: 0,
    ratingsUserTotal: null,
    comments: formData.comments || '',
    isGoogleRatingAvailable: false,
    isContactClaimed: false,
    ownerName: formData.ownerName || '',
    approvedByName: formData.approvedByName || '',

    documentsRequired: formData.documentsRequired || [],
    rulesAndRegulations: formData.rulesAndRegulations || [],
    pricingInformation: formData.pricingInformation || {
      headerTitle: '',
      headerDescription: '',
      plans: [],
    },
  };

  if (formData.cityId) payload.cityId = toPositiveInt(formData.cityId);
  if (formData.districtId) payload.districtId = toPositiveInt(formData.districtId);
  return payload;
}

function buildContactMultipartFormData(payload, formData, bannerFiles, logoFile) {
  const fd = new FormData();
  const p = payload;

  fd.append('title', p.title ?? '');
  fd.append('infoNote', p.infoNote ?? '');
  fd.append('description', p.description ?? '');
  fd.append('supportLanguages', typeof p.supportLanguages === 'string' ? p.supportLanguages : '');
  fd.append('callToAction', typeof p.callToAction === 'string' ? p.callToAction : '');

  (p.clientTypes || []).forEach((type) => fd.append('clientTypes', String(type)));
  (p.subCategoryId || []).forEach((id) => fd.append('subCategoryId', String(id)));
  (p.subCategoryIds || p.subCategoryId || []).forEach((id) => fd.append('subCategoryIds', String(id)));
  const subIdsForForm = p.subCategories || p.subCategoryIds || p.subCategoryId || [];
  (Array.isArray(subIdsForForm) ? subIdsForForm : []).forEach((id) => fd.append('subCategories', String(id)));


  (p.documentsRequired || []).forEach((item) => fd.append('documentsRequired', String(item)));
  (p.rulesAndRegulations || []).forEach((item) => fd.append('rulesAndRegulations', String(item)));
  if (p.pricingInformation != null) {
    if (typeof p.pricingInformation === 'object') {
      fd.append('pricingInformation', JSON.stringify(p.pricingInformation));
    } else {
      fd.append('pricingInformation', String(p.pricingInformation));
    }
  }

  fd.append('contactPerson', p.contactPerson ?? '');
  fd.append('address', p.address ?? '');

  fd.append('houseNumber', p.houseNumber != null ? String(p.houseNumber) : '');
  fd.append('postalCode', p.postalCode ?? '');
  fd.append('email', p.email ?? '');
  fd.append('phone', p.phone ?? '');
  fd.append('website', p.website ?? '');
  fd.append('whatsapp', p.whatsapp ?? '');
  if (p.longitude != null) fd.append('longitude', String(p.longitude));
  if (p.latitude != null) fd.append('latitude', String(p.latitude));
  fd.append('isActive', String(!!p.isActive));
  fd.append('isBookingAppointmentEnabled', String(!!p.isBookingAppointmentEnabled));
  fd.append('isOnlineBookingEnabled', String(!!p.isOnlineBookingEnabled));
  fd.append('isOfflineBookingEnabled', String(!!p.isOfflineBookingEnabled));
  fd.append('isChatEnabled', String(!!p.isChatEnabled));
  fd.append('openingHours', typeof p.openingHours === 'object' && p.openingHours !== null ? JSON.stringify(p.openingHours) : (p.openingHours ?? ''));
  fd.append('whatappText', p.whatappText ?? '');
  fd.append('categoryId', String(p.categoryId ?? ''));
  if (p.cityId != null) fd.append('cityId', String(p.cityId));
  if (p.districtId != null) fd.append('districtId', String(p.districtId));
  fd.append('approvalStatus', p.approvalStatus ?? 'PENDING');
  fd.append('approvalNote', p.approvalNote ?? '');
  fd.append('comments', p.comments ?? '');
  fd.append('ownerName', p.ownerName ?? '');
  fd.append('approvedByName', p.approvedByName ?? '');
  fd.append('rating', String(p.rating ?? 0));
  fd.append('isDistanceAvailable', String(!!p.isDistanceAvailable));
  fd.append('isGoogleRatingAvailable', String(!!p.isGoogleRatingAvailable));
  fd.append('isContactClaimed', String(!!p.isContactClaimed));
  fd.append('imageUrlArray', p.imageUrlArray != null ? JSON.stringify(p.imageUrlArray) : '');
  fd.append('contactRating', p.contactRating != null ? String(p.contactRating) : '');
  fd.append('ratingsUserTotal', p.ratingsUserTotal != null ? String(p.ratingsUserTotal) : '');

  // Append pictures (newly uploaded File objects)
  const picturesToUpload = bannerFiles.length > 0
    ? bannerFiles
    : (Array.isArray(formData.pictures) ? formData.pictures : []);
  picturesToUpload.forEach((file) => {
    if (typeof File !== 'undefined' && file instanceof File) {
      fd.append('pictures', file);
    }
  });

  // Append picturesKeep (kept existing picture URLs)
  const picturesKeepList = Array.isArray(formData.picturesKeep)
    ? formData.picturesKeep
    : [];
  picturesKeepList.forEach((url) => {
    if (url) fd.append('picturesKeep', String(url));
  });

  // Append picturesRemove (removed existing picture URLs)
  const picturesRemoveList = Array.isArray(formData.picturesRemove)
    ? formData.picturesRemove
    : [];
  picturesRemoveList.forEach((url) => {
    if (url) fd.append('picturesRemove', String(url));
  });

  if (logoFile) {
    fd.append('logoPicture', logoFile);
  } else if (p.logoPicture) {
    const lp = String(p.logoPicture);
    fd.append('logoPicture', lp);
    fd.append('LogoPicture', lp);
  }
  if (p.id != null) fd.append('id', String(p.id));
  return fd;
}

async function fetchFreshContactRecord(contact) {
  let contactResolved = transformContactFromApi(contact);
  const cid = contactResolved?.id ?? contactResolved?.contactId;
  if (cid == null) return contactResolved;
  const res = await contactsAPI.getContact(cid);
  const body = res?.data;
  const fresh = extractContactFromGetByIdResponse(body);
  if (fresh && body?.success !== false) {
    contactResolved = transformContactFromApi({ ...contactResolved, ...fresh });
  }
  return contactResolved;
}

function getSubcategoryChipDisplayCount(isExpanded, total) {
  if (isExpanded || total <= 5) return total;
  return 5;
}

function SubcategoryExpansionChip({ contactId, total, isExpanded, onToggle }) {
  if (total <= 5) return null;
  if (isExpanded) {
    return (
      <Chip
        label="View less"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onToggle(contactId);
        }}
        sx={{
          height: 24,
          fontSize: '0.75rem',
          bgcolor: '#f5f5f5',
          color: '#424242',
          fontWeight: 700,
          borderRadius: '6px',
          cursor: 'pointer',
          border: '1px solid #e0e0e0',
          '&:hover': { bgcolor: '#e0e0e0' },
        }}
      />
    );
  }
  return (
    <Chip
      label={`+${total - 5} more`}
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        onToggle(contactId);
      }}
      sx={{
        height: 24,
        fontSize: '0.75rem',
        bgcolor: '#FF8C42',
        color: '#fff',
        fontWeight: 700,
        borderRadius: '6px',
        cursor: 'pointer',
        '&:hover': { bgcolor: '#E06526' },
      }}
    />
  );
}

function getContactDialogPaperSx(isMobile) {
  const base = isMobile ? { borderRadius: 0, maxHeight: '100%' } : { borderRadius: 4, maxHeight: '90vh' };
  return {
    ...base,
    '& .MuiFormLabel-asterisk': {
      color: '#d32f2f !important',
      fontWeight: 700,
    },
    '& .MuiInputLabel-asterisk': {
      color: '#d32f2f !important',
      fontWeight: 700,
    },
  };
}

function getApprovalSuccessMessage(approvalStatus) {
  if (approvalStatus === 'APPROVED') return 'approved';
  return 'declined';
}

function getAddressFieldColor(formErrors, addressLoading, formData, addressSuggestions) {
  if (formErrors.address) return 'error.main';
  const address = String(formData.address || '');
  if (!addressLoading && address.length >= 3 && addressSuggestions.length === 0) {
    return 'error.main';
  }
  return '#d97706';
}

function requiresDeclineApprovalNote(status, note) {
  return status === 'DECLINE' && note.trim() === '';
}

function ContactsListShell({ isLoading, hasResults, loadingContent, emptyContent, children }) {
  if (isLoading) return loadingContent;
  if (hasResults) return children;
  return emptyContent;
}

const ACCENT_COLOR = '#FF8C42';
const GRAY_BORDER = '#e0e0e0';
/** Thumbnail tiles — subtle black shadow */
const IMG_SHADOW_THUMB = '0 2px 10px rgba(0, 0, 0, 0.14)';
/** Logo lightbox — stronger black shadow on the enlarged image */
const IMG_SHADOW_LIGHTBOX = '0 16px 48px rgba(0, 0, 0, 0.42)';
/** Single fetch size (no pagination UI) */
const CONTACTS_LIST_LIMIT = 500;

const DEMO_PREVIEW_DATA = {
  title: "Asma Fahrschule Berlin Prenzlauer Berg",
  contactPerson: "Asma Fahrschule",
  address: "Greifswalder Straße 207, 10405 Berlin, Deutschland pankow",
  phone: "+49 30 123456",
  email: "info@asma-fahrschule.de",
  website: "https://www.asma-fahrschule.de",
  whatsapp: "+49 30 123456",
  description: "A multi-branch driving school with 5 locations across Berlin (Wittenau, Pankow, Steglitz, Prenzlauer Berg, Mariendorf), offering training for car, motorcycle, trailer, and truck licenses. Slogan: \"As much as necessary, as little as possible.\"",
  supportLanguages: "English",
  subCategoryNames: ["Car(Klasse B)", "Motorcycle(A,A1,A2,AM)", "Car/Motorcycle(B+A1)"],
  openingHours: "Monday 09:00 - 17:00",
  documentsRequired: [
    "ID (Personalausweis)",
    "Eyesight test (Sehtest)",
    "First-aid course certificate (Erste-Hilfe-Kurs)",
    "Biometric passport photo"
  ],
  rulesAndRegulations: [
    "Consultation — Class",
    "documents",
    "appointments",
    "and best starting point are clarified",
    "Registration — Choose your branch",
    "get your personalized training plan",
    "Theory & Practice — Traffic rules",
    "vehicle handling",
    "safe driving behavior"
  ],
  bannerUrls: [
    "https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=500&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=500&auto=format&fit=crop"
  ],
  logoUrl: null,
  avatarInitials: "AF",
  isVerified: true,
  isChatEnabled: true,
  isBookingEnabled: true,
  pricingInformation: {
    headerTitle: 'Insurance Packages & What You Get',
    headerDescription: 'Official visa-compliant packages for Germany',
    plans: PRICING_PLANS,
  },
};

const Contacts = () => {
  const { t, language } = useLanguage();
  const { user, token } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const validateField = (name, value) => validateContactField(name, value, t);

  const [isLoading, setIsLoading] = useState(false);
  const [expandedContacts, setExpandedContacts] = useState(new Set());
  const toggleContactExpansion = (id) => {
    setExpandedContacts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [subCategories, setSubCategories] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [open, setOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState('');
  const [approvalNote, setApprovalNote] = useState('');
  const [contactToApprove, setContactToApprove] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const filteredContacts = useMemo(
    () => filterContactsList(contacts, statusFilter, searchText),
    [contacts, statusFilter, searchText]
  );
  const [activeTab, setActiveTab] = useState(0);
  /** Add-contact flow: 0 General → 1 Location → 2 Communication → 3 Business (submit) */
  const [createStep, setCreateStep] = useState(0);

  // Call to Action Popup Dialog state
  const [ctaDialogOpen, setCtaDialogOpen] = useState(false);
  const [ctaDialogContact, setCtaDialogContact] = useState(null);
  const [ctaDialogValue, setCtaDialogValue] = useState('');
  const [ctaDialogSubmitting, setCtaDialogSubmitting] = useState(false);

  const openCtaDialog = (contact) => {
    setCtaDialogContact(contact);
    setCtaDialogValue(contact?.callToAction || '');
    setCtaDialogOpen(true);
  };

  const closeCtaDialog = () => {
    if (!ctaDialogSubmitting) {
      setCtaDialogOpen(false);
      setCtaDialogContact(null);
      setCtaDialogValue('');
    }
  };

  const handleSaveCtaDialog = async () => {
    if (!ctaDialogContact) return;
    const cid = ctaDialogContact.id ?? ctaDialogContact.contactId;
    if (!cid) return;
    const updatedCallToAction = ctaDialogValue.trim();
    if (updatedCallToAction.length > 30) {
      toast.error(t('pages.contacts.validation.callToActionLength') || 'Call to Action cannot exceed 30 characters.');
      return;
    }
    setCtaDialogSubmitting(true);
    try {
      const updatedContact = { ...ctaDialogContact, callToAction: updatedCallToAction };
      const payload = buildContactSubmitPayload(updatedContact, [], null, updatedContact.categoryId);
      const formDataObj = buildContactMultipartFormData(payload, updatedContact, [], null);
      await contactsAPI.updateContactMultipart(cid, formDataObj);
      toast.success(t('layout.toast.nameUpdated') || 'Call to Action updated successfully.');
      setCtaDialogOpen(false);
      setCtaDialogContact(null);
      setCtaDialogValue('');
      await fetchContacts();
    } catch (err) {
      toast.error(getContactsApiErrorMessage(err?.response?.data) || 'Failed to update Call to Action');
    } finally {
      setCtaDialogSubmitting(false);
    }
  };

  // Mobile preview states
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewContact, setPreviewContact] = useState(null);
  const [activeBannerIndex, setActiveBannerIndex] = useState(0);
  const [infoNoteExpanded, setInfoNoteExpanded] = useState(false);
  const [planExpanded, setPlanExpanded] = useState(false);
  const [categoriesExpanded, setCategoriesExpanded] = useState(false);
  const [hoursExpanded, setHoursExpanded] = useState(false);
  const bannerScrollRef = useRef(null);

  const handleOpenPreview = (contact) => {
    setPreviewContact(contact);
    setActiveBannerIndex(0);
    setInfoNoteExpanded(false);
    setPlanExpanded(false);
    setCategoriesExpanded(false);
    setHoursExpanded(false);
    setPreviewDialogOpen(true);
  };

  // Card View preview states (separate from full mobile view)
  const [cardViewDialogOpen, setCardViewDialogOpen] = useState(false);
  const [cardViewContact, setCardViewContact] = useState(null);

  const handleOpenCardView = (contact) => {
    setCardViewContact(contact);
    setCardViewDialogOpen(true);
  };

  const handleCloseCardView = () => {
    setCardViewDialogOpen(false);
    setCardViewContact(null);
  };

  const cardViewData = useMemo(() => {
    const contactResolved = cardViewContact || DEMO_PREVIEW_DATA;
    const bannerStr = contactResolved.bannerImages || contactResolved.imageUrls || '';
    const banners = typeof bannerStr === 'string'
      ? bannerStr.split(',').map((x) => x.trim()).filter(Boolean)
      : Array.isArray(bannerStr) ? bannerStr : [];

    const subCatDisplayItems = getSubcategoryDisplayItemsForContact(contactResolved, subCategories);
    const subCategoryNames = subCatDisplayItems.map((item) => item.label);

    const districtName =
      contactResolved.districtName ||
      contactResolved.district_Name ||
      contactResolved.districtTitle ||
      (typeof contactResolved.district === 'object' ? (contactResolved.district?.name || contactResolved.district?.title) : contactResolved.district) ||
      districts.find((d) => Number(d.id ?? d.districtId) === Number(contactResolved.districtId))?.name ||
      '';

    const streetAndHouse = [contactResolved.address, contactResolved.houseNumber].filter(Boolean).join(' ').trim();
    const districtAndPostal = [districtName, contactResolved.postalCode].filter(Boolean).join(', ').trim();
    const addressText = [streetAndHouse, districtAndPostal].filter(Boolean).join(', ').trim();

    const pricing = parsePricingInformationField(contactResolved.pricingInformation);
    return {
      title: contactResolved.title || '',
      contactPerson: contactResolved.contactPerson || '',
      addressText: addressText,
      address: addressText,
      phone: contactResolved.phone || '',
      email: contactResolved.email || '',
      website: contactResolved.website || '',
      whatsapp: contactResolved.whatsapp || '',
      description: contactResolved.description || '',
      callToAction: contactResolved.callToAction || '',
      infoNote: contactResolved.infoNote || '',
      plans: pricing.plans || [],
      pricingInformation: pricing,
      subCatChips: subCatDisplayItems,
      subCategoryNames: subCategoryNames.length > 0 ? subCategoryNames : null,
      bannerUrls: banners,
      logoUrl: contactResolved.logoPicture || contactResolved.profileImageUrl || null,
      avatarInitials: (contactResolved.title || '').trim().charAt(0).toUpperCase() || 'C',
      isVerified: contactResolved.approvalStatus === 'APPROVED' || contactResolved.isContactClaimed,
      isActive: contactResolved.isActive !== undefined ? Boolean(contactResolved.isActive) : true,
      isChatEnabled: contactResolved.isChatEnabled !== undefined ? Boolean(contactResolved.isChatEnabled) : true,
      ratingInfo: getContactRatingInfo(contactResolved),
      visitCount: contactResolved.visitCount ?? contactResolved.visit_count ?? contactResolved.views ?? contactResolved.viewCount ?? 0,
      districtName: districtName || 'Berlin',
      isOnline: Boolean(contactResolved.isOnlineBookingEnabled || contactResolved.isOnlineAvailable),
      isInPerson: Boolean(contactResolved.isOfflineBookingEnabled || contactResolved.isInPersonAvailable || (!contactResolved.isOnlineBookingEnabled && !contactResolved.isOnlineAvailable)),
      thumbnailUrl: contactResolved.logoPicture || contactResolved.profileImageUrl || (banners.length > 0 ? banners[0] : null),
      distance: contactResolved.distance || '18.1 km',
    };
  }, [cardViewContact, subCategories, districts]);
  const previewData = useMemo(() => {
    if (!previewContact) {
      return DEMO_PREVIEW_DATA;
    }
    const contactResolved = previewContact;

    const bannerStr = contactResolved.bannerImages || contactResolved.imageUrls || '';
    const banners = typeof bannerStr === 'string'
      ? bannerStr.split(',').map((x) => x.trim()).filter(Boolean)
      : Array.isArray(bannerStr) ? bannerStr : [];

    const subCatDisplayItems = getSubcategoryDisplayItemsForContact(contactResolved, subCategories);
    const subCategoryNames = subCatDisplayItems.map(item => item.label);

    const supportLanguages = Array.isArray(contactResolved.supportLanguages)
      ? contactResolved.supportLanguages.join(', ')
      : typeof contactResolved.supportLanguages === 'string'
        ? contactResolved.supportLanguages
        : '';

    const districtName =
      contactResolved.districtName ||
      contactResolved.district_Name ||
      contactResolved.districtTitle ||
      (typeof contactResolved.district === 'object' ? (contactResolved.district?.name || contactResolved.district?.title) : contactResolved.district) ||
      districts.find((d) => Number(d.id ?? d.districtId) === Number(contactResolved.districtId))?.name ||
      '';

    const streetAndHouse = [contactResolved.address, contactResolved.houseNumber].filter(Boolean).join(' ').trim();
    const districtAndPostal = [districtName, contactResolved.postalCode].filter(Boolean).join(', ').trim();
    const fullAddress = [streetAndHouse, districtAndPostal, 'Deutschland'].filter(Boolean).join(', ').trim();

    return {
      title: contactResolved.title || '',
      contactPerson: contactResolved.contactPerson || '',
      address: fullAddress || `${contactResolved.address || ''}, Berlin, Deutschland`,
      phone: contactResolved.phone || '',
      email: contactResolved.email || '',
      website: contactResolved.website || '',
      whatsapp: contactResolved.whatsapp || '',
      description: contactResolved.description || '',
      infoNote: contactResolved.infoNote || '',
      supportLanguages: supportLanguages,
      callToAction: contactResolved.callToAction || '',
      subCategoryNames: subCategoryNames.length > 0 ? subCategoryNames : null,
      openingHours: contactResolved.openingHours || '',
      documentsRequired: contactResolved.documentsRequired || [],
      rulesAndRegulations: contactResolved.rulesAndRegulations || [],
      bannerUrls: banners,
      logoUrl: contactResolved.logoPicture || contactResolved.profileImageUrl || null,
      avatarInitials: (contactResolved.title || '').trim().charAt(0).toUpperCase() || 'C',
      isVerified: contactResolved.approvalStatus === 'APPROVED' || contactResolved.isContactClaimed,
      isChatEnabled: contactResolved.isChatEnabled !== undefined ? Boolean(contactResolved.isChatEnabled) : true,
      isBookingEnabled: contactResolved.isBookingAppointmentEnabled || contactResolved.isOnlineBookingEnabled || contactResolved.isOfflineBookingEnabled,
      pricingInformation: parsePricingInformationField(contactResolved.pricingInformation),
      districtName: districtName || 'Charlottenburg-Wilmersdorf',
      distance: contactResolved.distance || '25.9 km',
      ratingInfo: getContactRatingInfo(contactResolved),
    };
  }, [previewContact, subCategories, districts]);

  const [bannerFiles, setBannerFiles] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [logoLightboxOpen, setLogoLightboxOpen] = useState(false);
  const [logoLightboxSrc, setLogoLightboxSrc] = useState(null);

  const openLogoLightbox = (src) => {
    if (!src) return;
    setLogoLightboxSrc(src);
    setLogoLightboxOpen(true);
  };

  const closeLogoLightbox = () => {
    setLogoLightboxOpen(false);
    setLogoLightboxSrc(null);
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    infoNote: '',
    subCategoryId: [],
    contactPerson: '',
    address: '',
    houseNumber: '',
    postalCode: '',
    cityId: '',
    districtId: '',
    openingHours: '',
    email: '',
    phone: '',
    website: '',
    whatsapp: '',
    whatsappText: '',
    longitude: '',
    latitude: '',
    approvalStatus: 'PENDING',
    approvalNote: '',
    logoPicture: '',
    isActive: true,
    supportLanguages: '',
    callToAction: '',
    clientTypes: '',
    ownerName: '',
    approvedByName: '',
    comments: '',
    isBookingAppointmentEnabled: false,
    isOnlineBookingEnabled: false,
    isOfflineBookingEnabled: true,
    isChatEnabled: true,
  });

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const addressSearchTimerRef = React.useRef(null);

  const handleAddressSearch = async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      return;
    }

    if (addressSearchTimerRef.current) clearTimeout(addressSearchTimerRef.current);

    addressSearchTimerRef.current = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const url = `https://geocode.search.hereapi.com/v1/geocode?q=${encodeURIComponent(query)}&limit=5&in=countryCode:DEU&apiKey=6Y5UiHisZ3Ig9XwBJT20IwJMXar22qUlLWDpXYVKjw0`;
        const response = await fetch(url);
        const data = await response.json();
        setAddressSuggestions(data.items || []);
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
      } finally {
        setAddressLoading(false);
      }
    }, 500);
  };

  const [formErrors, setFormErrors] = useState({});

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  const [contactDialogLoading, setContactDialogLoading] = useState(false);

  /** Auth JWT first; when editing/viewing a contact, fall back to its category so the API can run */
  const fetchSubCategories = async () => {
    const id = getServiceCategoryIdForSubcategories(user, token, selectedContact);
    if (id == null || id === '') {
      setSubCategories([]);
      return;
    }
    try {
      const response = await subCategoriesAPI.getSubCategories(id);
      const body = response?.data;
      const list =
        body?.payload ??
        body?.data?.data ??
        body?.data ??
        (Array.isArray(body) ? body : []);
      setSubCategories(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error fetching subcategories:', err);
      setSubCategories([]);
    }
  };

  const fetchDistricts = async (cityId) => {
    if (!cityId) {
      setDistricts([]);
      return;
    }
    try {
      const response = await districtsAPI.getDistricts(cityId, language || 'en');
      const list = response?.data?.data || [];
      setDistricts(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error fetching districts:', err);
    }
  };

  const handleSubCategoryChange = (e) => {
    const { value } = e.target;
    const arr = normalizeSubCategorySelectionValue(value);
    const subCategoryId = [...new Set(arr.filter((n) => Number.isFinite(n) && n > 0))];
    setFormData((prev) => ({ ...prev, subCategoryId }));
    if (formErrors.subCategoryId) {
      setFormErrors(prev => ({ ...prev, subCategoryId: '' }));
    }
  };

  const fetchContacts = async () => {
    setIsLoading(true);

    try {
      const response = await contactsAPI.getContacts(
        '',
        '',
        0,
        CONTACTS_LIST_LIMIT
      );

      if (response?.data?.success === true) {
        const contactsList = response.data.data || [];
        const total = response.data.totalRecord ?? response.data.data?.length ?? 0;

        const transformedContacts = contactsList.map((c) => transformContactFromApi(c));

        setContacts(transformedContacts);
        setTotalCount(total);
      } else {
        setContacts([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    fetchSubCategories();
  }, [user, token, selectedContact]);

  useEffect(() => {
    const authCityId = getCityIdFromAuth(user, token);
    if (authCityId) {
      fetchDistricts(authCityId);
    }
  }, [user, token]);

  const handleOpen = async (contact = null) => {
    setActiveTab(0);
    setCreateStep(0);
    setBannerFiles([]);
    setLogoFile(null);

    if (contact) {
      setOpen(true);
      setContactDialogLoading(true);
      try {
        let contactResolved;
        try {
          contactResolved = await fetchFreshContactRecord(contact);
        } catch (error) {
          console.error('GET contact by id failed:', error);
          toast.error(
            'Could not load the latest contact from the server. Showing list data.',
            { position: 'top-center' }
          );
          contactResolved = transformContactFromApi(contact);
        }
        setSelectedContact(contactResolved);
        setFormData(buildFormDataFromContact(contactResolved));

        if (contactResolved.cityId) {
          await fetchDistricts(contactResolved.cityId);
        }
        await fetchSubCategories();
      } finally {
        setContactDialogLoading(false);
      }
    } else {
      setSelectedContact(null);
      setFormData(getEmptyContactForm(getCityIdFromAuth(user, token) || ''));

      const authCityId = getCityIdFromAuth(user, token);
      if (authCityId) {
        await fetchDistricts(authCityId);
      }
      await fetchSubCategories();
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setContactDialogLoading(false);
    setCreateStep(0);
    setSelectedContact(null);
    setBannerFiles([]);
    setLogoFile(null);
    setLogoLightboxOpen(false);
    setLogoLightboxSrc(null);
    setFormData(getEmptyContactForm());
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (name === 'cityId') {
      fetchDistricts(value);
      setFormData(prev => ({ ...prev, districtId: '' }));
    }
  };

  const isValidImageFile = (file) => {
    if (!file) return false;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    const allowedExtensions = ['png', 'jpg', 'jpeg'];
    const isTypeValid = allowedTypes.includes((file.type || '').toLowerCase());
    const ext = (file.name || '').split('.').pop()?.toLowerCase();
    const isExtValid = allowedExtensions.includes(ext);
    return isTypeValid || isExtValid;
  };

  const handleBannerFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) {
      const validFiles = files.filter(isValidImageFile);
      if (validFiles.length < files.length) {
        toast.error('Only PNG, JPG, and JPEG image formats are allowed.', { position: 'top-center' });
      }
      if (validFiles.length) {
        setBannerFiles((prev) => [...prev, ...validFiles]);
        setFormData((prev) => ({
          ...prev,
          pictures: [...(prev.pictures || []), ...validFiles]
        }));
      }
    }
    e.target.value = '';
  };

  const removeBannerFileAt = (index) => {
    setBannerFiles((prev) => prev.filter((_, i) => i !== index));
    setFormData((prev) => ({
      ...prev,
      pictures: (prev.pictures || []).filter((_, i) => i !== index)
    }));
  };

  const removeSavedBannerAt = (index) => {
    setFormData((prev) => {
      const list = Array.isArray(prev.picturesKeep) ? prev.picturesKeep : [];
      if (index < 0 || index >= list.length) return prev;
      const removedItem = list[index];
      const next = list.filter((_, i) => i !== index);
      return {
        ...prev,
        picturesKeep: next,
        picturesRemove: [...(prev.picturesRemove || []), removedItem]
      };
    });
  };

  const clearSavedLogo = () => {
    setFormData((prev) => ({ ...prev, logoPicture: '' }));
  };

  const removeLogoFile = () => {
    setLogoFile(null);
  };

  const bannerPreviewUrls = useMemo(
    () => bannerFiles.map((file) => URL.createObjectURL(file)),
    [bannerFiles]
  );

  useEffect(() => {
    return () => {
      bannerPreviewUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [bannerPreviewUrls]);

  const logoPreviewUrl = useMemo(
    () => (logoFile ? URL.createObjectURL(logoFile) : null),
    [logoFile]
  );

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    };
  }, [logoPreviewUrl]);

  const handleLogoFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      if (!isValidImageFile(f)) {
        toast.error('Only PNG, JPG, and JPEG image formats are allowed.', { position: 'top-center' });
        e.target.value = '';
        return;
      }
      setLogoFile(f);
    }
    e.target.value = '';
  };

  const closeApprovalDialog = () => {
    setApprovalDialogOpen(false);
    setContactToApprove(null);
    setApprovalStatus('');
    setApprovalNote('');
  };

  const handleApproval = async () => {
    if (requiresDeclineApprovalNote(approvalStatus, approvalNote)) {
      toast.error('Approval note is required when declining a contact', { position: 'top-center' });
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await contactsAPI.updateContactApproval({
        id: contactToApprove.id,
        approvalStatus: approvalStatus,
        approvalNote: approvalNote.trim()
      });

      if (response?.data?.success === true) {
        toast.success(`Contact ${getApprovalSuccessMessage(approvalStatus)} successfully`, { position: 'top-center' });
        closeApprovalDialog();
        fetchContacts();
      } else {
        toast.error('Error updating approval status: ' + (response?.data?.message || 'Unknown error'), { position: 'top-center' });
      }
    } catch (error) {
      console.error('Error updating approval status:', error);
      toast.error('Error updating approval status: ' + (error.message || 'Unknown error'), { position: 'top-center' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    const newErrors = {};
    const fieldNames = [
      'title', 'contactPerson', 'description', 'infoNote', 'subCategoryId', 'supportLanguages',
      'address', 'houseNumber', 'postalCode', 'districtId',
      'email', 'phone', 'website', 'whatsapp', 'openingHours', 'callToAction',
    ];

    fieldNames.forEach((name) => {
      newErrors[name] = validateField(name, formData[name]);
    });

    setFormErrors(newErrors);

    const firstError = findFirstValidationError(newErrors);
    if (firstError) {
      toast.error(firstError, { position: 'top-center' });
      return;
    }

    const pricing = parsePricingInformationField(formData.pricingInformation);
    if (pricing.plans.length > 0) {
      if (!String(pricing.headerTitle || '').trim() || !String(pricing.headerDescription || '').trim()) {
        toast.error('Package title and package description are required when pricing plans are added.', { position: 'top-center' });
        return;
      }
      if (pricing.headerTitle.length > 50 || pricing.headerDescription.length > 150) {
        toast.error('Package title must be 1–50 characters and package description 1–150 characters.', { position: 'top-center' });
        return;
      }
      const invalidPlan = pricing.plans.some((plan) => (
        !String(plan?.title || '').trim()
        || !String(plan?.description || '').trim()
        || String(plan?.title || '').length > 50
        || String(plan?.description || '').length > 500
        || String(plan?.badge || '').length > 30
        || String(plan?.price || '').length > 50
        || String(plan?.period || '').length > 50
        || String(plan?.hint || '').length > 100
        || (Array.isArray(plan?.highlights) && plan.highlights.some((highlight) => String(highlight || '').length > 50))
      ));
      if (invalidPlan) {
        toast.error('Each pricing plan needs a title (1–50 characters) and description (1–500 characters). Optional fields must meet their character limits.', { position: 'top-center' });
        return;
      }
    }

    const invalidPricing = (formData.pricingInformation?.plans || []).some((plan) => {
      const price = String(plan?.price ?? '').trim();
      return price !== '' && (!Number.isFinite(Number(price)) || Number(price) < 0);
    });
    if (invalidPricing) {
      toast.error('Enter a valid non-negative price, or leave pricing blank.', { position: 'top-center' });
      return;
    }

    const categoryIdResolved = toPositiveInt(getServiceCategoryIdFromAuth(user, token));
    if (!categoryIdResolved) {
      toast.error('Your account must have a valid service category (categoryId / serviceCategoryId in profile or JWT) to save a contact.', { position: 'top-center' });
      return;
    }


    setIsSubmitting(true);
    try {
      const payload = buildContactSubmitPayload(formData, bannerFiles, logoFile, categoryIdResolved);
      const formBody = buildContactMultipartFormData(payload, formData, bannerFiles, logoFile);

      if (selectedContact) {
        payload.id = selectedContact.id;
        const response = await contactsAPI.updateContactMultipart(selectedContact.id, formBody);
        if (response?.data?.success === true) {
          toast.success('Contact updated successfully', { position: 'top-center' });
          handleClose();
          fetchContacts();
        } else {
          toast.error('Error updating contact: ' + getContactsApiErrorMessage(response?.data), { position: 'top-center' });
        }
      } else {
        const response = await contactsAPI.createContactMultipart(formBody);
        if (response?.data?.success === true) {
          toast.success('Contact created successfully', { position: 'top-center' });
          handleClose();
          fetchContacts();
        } else {
          toast.error('Error creating contact: ' + getContactsApiErrorMessage(response?.data), { position: 'top-center' });
        }
      }
    } catch (error) {
      console.error('Error saving contact:', error);
      const msg = error?.response?.data && getContactsApiErrorMessage(error.response.data);
      toast.error('Error saving contact: ' + (msg || error.message || 'Unknown error'), { position: 'top-center' });
    } finally {
      setIsSubmitting(false);
    }
  };



  const handleToggleActiveStatus = async (contact) => {
    const contactId = contact?.id ?? contact?.contactId;
    if (!contactId) {
      toast.error('Contact ID is required for status update', { position: 'top-center' });
      return;
    }
    const nextIsActive = !Boolean(contact.isActive);
    try {
      setIsLoading(true);
      await contactsAPI.updateContactStatus(contactId, nextIsActive);
      toast.success(nextIsActive ? 'Contact activated' : 'Contact deactivated', { position: 'top-center' });
      fetchContacts();
    } catch (error) {
      console.error('Error updating contact status:', error);
      toast.error(
        getContactsApiErrorMessage(error?.response?.data) || error.message || 'Failed to update contact status',
        { position: 'top-center' }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const ro = false; // read-only functionality removed
  const viewMode = false;

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return '#10b981';
      case 'REJECTED':
      case 'DECLINED':
        return '#ef4444';
      default:
        return '#f59e0b';
    }
  };

  const subCategoryIds = (
    Array.isArray(formData.subCategoryId) ? formData.subCategoryId : []
  ).map(Number).filter((n) => Number.isFinite(n) && n > 0);
  const str = (v) => String(v ?? '').trim();

  /** Service category for subcategories dropdown (auth, JWT, or contact being edited) */
  const effectiveServiceCategoryId = getServiceCategoryIdForSubcategories(user, token, selectedContact);

  const isCreateWizard = selectedContact == null;
  const stepIndex = isCreateWizard ? createStep : activeTab;

  const getCreateWizardStepError = (step) => {
    const { error, errors } = collectCreateWizardStepValidation(step, formData, validateField, {
      bannerFiles,
      logoFile,
      categoryIdResolved: toPositiveInt(getServiceCategoryIdFromAuth(user, token)),
    });
    setFormErrors((prev) => ({ ...prev, ...errors }));
    return error;
  };

  /** One entry per subcategory for list cards and chips (multiple IDs → multiple labels). */
  const getSubcategoryDisplayItems = (contact) =>
    getSubcategoryDisplayItemsForContact(contact, subCategories);

  const handleWizardNext = () => {
    const err = getCreateWizardStepError(createStep);
    if (err) {
      toast.error(err, { position: 'top-center' });
      return;
    }
    setCreateStep((s) => Math.min(4, s + 1));
  };

  const handleWizardPrevious = () => {
    setCreateStep((s) => Math.max(0, s - 1));
  };

  // Contact status counts for summary cards (from current contacts list)
  const contactCounts = {
    total: totalCount,
    approved: contacts.filter((c) => c.approvalStatus === 'APPROVED').length,
    pending: contacts.filter((c) => c.approvalStatus === 'PENDING').length,
    declined: contacts.filter((c) => DECLINED_FILTER_STATUSES.includes(c.approvalStatus)).length,
    inactive: contacts.filter((c) => c.isActive === false).length,
  };

  return (
    <>
      <Box sx={{ width: '100%', p: { xs: 1, sm: 2 }, pr: { xs: 1, sm: 2 } }}>
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 4,
            p: { xs: 2, sm: 4 },
            borderRadius: { xs: 2, sm: 4 },
            background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
            border: '1px solid rgba(255, 140, 66, 0.2)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}
        >
          <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, letterSpacing: '-0.03em', color: '#37373a' }}>
              {t('pages.contacts.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 500 }}>
              {t('pages.contacts.subtitle')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            fullWidth={isMobile}
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              background: '#FF8C42',
              boxShadow: '0 4px 15px rgba(255, 140, 66, 0.3)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: '#E06526',
                transform: 'translateY(-2px) scale(1.02)',
                boxShadow: '0 8px 25px rgba(19, 88, 163, 0.4)',
              },
              '&:active': { transform: 'translateY(0)' }
            }}
          >
            {t('pages.contacts.dialog.addContact')}
          </Button>
        </Box>

        {/* Status Summary Cards — light pastel colors (per image), large number, label, subtle background icon */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper
              elevation={0}
              onClick={() => setStatusFilter('')}
              sx={{
                padding: { xs: '16px', sm: '24px' }, height: '95%', borderRadius: 4, position: 'relative', overflow: 'hidden',
                bgcolor: '#e8ecf8', border: '1px solid',
                borderColor: statusFilter === '' ? '#34498e' : 'rgba(52, 73, 142, 0.1)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(52, 73, 142, 0.12)' },
              }}
            >
              <ListIcon sx={{ position: 'absolute', right: -10, bottom: -10, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: '#34498e' }} />
              <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 800, mb: 1, color: '#34498e' }}>{contactCounts.total}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#34498e', display: 'block', lineHeight: 1.2 }}>{t('pages.contacts.stats.total')}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper
              elevation={0}
              onClick={() => setStatusFilter('APPROVED')}
              sx={{
                padding: { xs: '16px', sm: '24px' }, height: '95%', borderRadius: 4, position: 'relative', overflow: 'hidden',
                bgcolor: '#e6fae6', border: '1px solid',
                borderColor: statusFilter === 'APPROVED' ? '#38963c' : 'rgba(56, 150, 60, 0.1)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(56, 150, 60, 0.12)' },
              }}
            >
              <CheckCircle sx={{ position: 'absolute', right: -10, bottom: -10, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: '#38963c' }} />
              <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 800, mb: 1, color: '#38963c' }}>{contactCounts.approved}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#38963c', display: 'block', lineHeight: 1.2 }}>{t('pages.contacts.stats.approved')}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper
              elevation={0}
              onClick={() => setStatusFilter('PENDING')}
              sx={{
                padding: { xs: '16px', sm: '24px' }, height: '95%', borderRadius: 4, position: 'relative', overflow: 'hidden',
                bgcolor: '#fff7e6', border: '1px solid',
                borderColor: statusFilter === 'PENDING' ? '#f09630' : 'rgba(240, 150, 48, 0.1)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(240, 150, 48, 0.12)' },
              }}
            >
              <AccessTime sx={{ position: 'absolute', right: -10, bottom: -10, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: '#f09630' }} />
              <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 800, mb: 1, color: '#f09630' }}>{contactCounts.pending}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#f09630', display: 'block', lineHeight: 1.2 }}>{t('pages.contacts.stats.pending')}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper
              elevation={0}
              onClick={() => setStatusFilter('DECLINED')}
              sx={{
                padding: { xs: '16px', sm: '24px' }, height: '95%', borderRadius: 4, position: 'relative', overflow: 'hidden',
                bgcolor: '#ffe6e6', border: '1px solid',
                borderColor: statusFilter === 'DECLINED' ? '#dc3545' : 'rgba(220, 53, 69, 0.1)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(220, 53, 69, 0.12)' },
              }}
            >
              <Cancel sx={{ position: 'absolute', right: -10, bottom: -10, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: '#dc3545' }} />
              <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 800, mb: 1, color: '#dc3545' }}>{contactCounts.declined}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#dc3545', display: 'block', lineHeight: 1.2 }}>{t('pages.contacts.stats.declined')}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4} md={2.4}>
            <Paper
              elevation={0}
              onClick={() => setStatusFilter('INACTIVE')}
              sx={{
                padding: { xs: '16px', sm: '24px' }, height: '95%', borderRadius: 4, position: 'relative', overflow: 'hidden',
                bgcolor: '#eaf5ff', border: '1px solid',
                borderColor: statusFilter === 'INACTIVE' ? '#34498e' : 'rgba(52, 73, 142, 0.1)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(52, 73, 142, 0.12)' },
              }}
            >
              <InactiveIcon sx={{ position: 'absolute', right: -10, bottom: -10, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: '#34498e' }} />
              <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 800, mb: 1, color: '#34498e' }}>{contactCounts.inactive}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#34498e', display: 'block', lineHeight: 1.2 }}>{t('pages.contacts.stats.inactive')}</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Filter Section */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              placeholder={t('pages.contacts.searchPlaceholder')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                sx: { borderRadius: '12px', bgcolor: 'white' }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}>
              <InputLabel>{t('pages.contacts.filter.approvalStatus')}</InputLabel>
              <Select
                value={statusFilter}
                label={t('pages.contacts.filter.approvalStatus')}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value=""><em>{t('pages.contacts.filter.allStatuses')}</em></MenuItem>
                <MenuItem value="APPROVED">{t('pages.contacts.status.approved')}</MenuItem>
                <MenuItem value="PENDING">{t('pages.contacts.status.pending')}</MenuItem>
                <MenuItem value="DECLINED">{t('pages.contacts.status.declined')}</MenuItem>
                <MenuItem value="INACTIVE">{t('pages.contacts.status.inactive')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterAltOff />}
              onClick={() => {
                setSearchText('');
                setStatusFilter('');
              }}
              sx={{
                height: '56px',
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                borderWidth: '2px',
                '&:hover': { borderWidth: '2px' }
              }}
            >
              {t('pages.contacts.clearFilters')}
            </Button>
          </Grid>
        </Grid>

        {/* Content Section */}
        <ContactsListShell
          isLoading={isLoading}
          hasResults={filteredContacts.length > 0}
          loadingContent={(
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={40} thickness={4} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t('pages.contacts.loadingList')}
              </Typography>
            </Box>
          )}
          emptyContent={
            contacts.length === 0 ? (
              <Box
                sx={{
                  p: 6,
                  textAlign: 'center',
                  bgcolor: '#ffffff',
                  borderRadius: 4,
                  border: '2px dashed #e2e8f0',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255, 140, 66, 0.1)',
                    color: ACCENT_COLOR,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 32 }} />
                </Box>
                <Box sx={{ maxWidth: 450 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
                    No Services Added Yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    You haven't listed any services or branches yet. Start by adding your first service to showcase your business.
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpen(null)}
                    sx={{
                      background: ACCENT_COLOR,
                      color: '#fff',
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: '10px',
                      px: 3,
                      '&:hover': { background: '#E06526' }
                    }}
                  >
                    Add Your First Service
                  </Button>

                  <Tooltip title="View Demo Mobile Listing Preview">
                    <Button
                      variant="outlined"
                      startIcon={<SmartphoneIcon />}
                      onClick={() => handleOpenPreview(null)}
                      sx={{
                        color: '#1976d2',
                        borderColor: '#1976d2',
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: '10px',
                        px: 3,
                        '&:hover': { borderColor: '#1565c0', bgcolor: 'rgba(25, 118, 210, 0.04)' }
                      }}
                    >
                      Demo Mobile Preview
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 8, textAlign: 'center', bgcolor: '#fbfbfb', borderRadius: 4, border: '2px dashed #e0e0e0', color: '#888', width: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  No contacts found
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Try adjusting your search or filters to find what you're looking for.
                </Typography>
              </Box>
            )
          }
        >
          <Grid container spacing={3}>
            {[...filteredContacts]
              .sort((a, b) => Number(Boolean(b?.isActive)) - Number(Boolean(a?.isActive)))
              .map((contact) => {
                const subCatChips = getSubcategoryDisplayItems(contact);
                return (
                  <Grid item xs={12} sm={6} lg={4} key={contact.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '16px',
                        border: '1px solid #E2E8F0',
                        position: 'relative',
                        overflow: 'hidden',
                        background: '#ffffff',
                        boxShadow: '0 4px 20px rgba(15, 23, 42, 0.04)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          borderColor: '#FF8C42',
                          boxShadow: '0 16px 36px rgba(255, 140, 66, 0.14)',
                          '& .bg-accent': { transform: 'scale(1.2) translate(-10%, -10%)' },
                        },
                      }}
                    >
                      <Box
                        className="bg-accent"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          width: 120,
                          height: 120,
                          background: 'radial-gradient(circle, rgba(255, 140, 66, 0.08) 0%, rgba(255, 140, 66, 0) 70%)',
                          borderRadius: '0 0 0 100%',
                          transition: 'transform 0.5s ease',
                          zIndex: 0,
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, zIndex: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#0F172A', lineHeight: 1.3, mb: 0.5 }} noWrap>
                            {contact.title}
                          </Typography>

                          {/* Contact Person & Total Visits (Compact Inline) */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                            {contact.contactPerson && (
                              <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', color: '#64748B', fontWeight: 600, fontSize: '0.78rem' }}>
                                <PeopleIcon sx={{ fontSize: 14, mr: 0.5, color: '#94A3B8' }} /> {contact.contactPerson}
                              </Typography>
                            )}
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.4,
                                px: 0.75,
                                py: 0.2,
                                borderRadius: '6px',
                                bgcolor: '#FFF7ED',
                                border: '1px solid #FFEDD5',
                              }}
                            >
                              <Visibility sx={{ fontSize: 13, color: '#FF8C42' }} />
                              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.73rem', color: '#D97706', lineHeight: 1.2 }}>
                                Total Visits: <span style={{ fontWeight: 800, color: '#EA580C' }}>{contact.visitCount ?? contact.visit_count ?? contact.views ?? contact.viewCount ?? 0}</span>
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Top Right Side: Active Status & Rating & Visit Count */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75, flexShrink: 0 }}>
                          {/* Active & Approval Chips */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <Chip
                              label={contact.isActive ? t('common.active') : t('common.inactive')}
                              sx={{
                                bgcolor: contact.isActive ? '#DCFCE7' : '#F1F5F9',
                                color: contact.isActive ? '#15803D' : '#475569',
                                fontWeight: 700,
                                borderRadius: '8px',
                                fontSize: '0.7rem',
                                height: 22,
                                border: '1px solid',
                                borderColor: contact.isActive ? '#BBF7D0' : '#E2E8F0',
                              }}
                              size="small"
                            />
                            {(() => {
                              const approval = contact.approvalStatus || 'PENDING';
                              const c = getStatusColor(approval);
                              const declined = isDeclinedApprovalStatus(approval);
                              let bgcolor = '#FEF3C7';
                              if (approval === 'APPROVED') {
                                bgcolor = '#D1FAE5';
                              } else if (declined) {
                                bgcolor = '#FEE2E2';
                              }
                              return (
                                <Chip
                                  label={approval}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    borderRadius: '8px',
                                    color: c,
                                    bgcolor,
                                    border: `1px solid ${c}40`,
                                  }}
                                />
                              );
                            })()}
                          </Box>

                          {/* Rating Star Display */}
                          {(() => {
                            const ratingInfo = getContactRatingInfo(contact);
                            if (!ratingInfo.hasRating) return null;
                            return (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                  {renderFiveStarRating(ratingInfo.averageRating, 14)}
                                </Box>
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: '0.82rem',
                                    fontWeight: 800,
                                    color: '#D97706',
                                    lineHeight: 1,
                                    display: 'inline-flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  {ratingInfo.averageRating.toFixed(1)}
                                </Typography>
                              </Box>
                            );
                          })()}
                        </Box>
                      </Box>

                      {/* Subcategories Chips (Wrapped 2-line display + Read More) */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          width: '100%',
                          mb: 1.5,
                          zIndex: 1,
                          py: 0.25,
                        }}
                      >
                        {(() => {
                          if (subCatChips.length === 0) {
                            return (
                              <Chip
                                label="—"
                                size="small"
                                sx={{ height: 22, fontSize: '0.72rem', bgcolor: '#F1F5F9', color: '#64748B', borderRadius: '6px' }}
                              />
                            );
                          }

                          const isExpanded = expandedContacts.has(contact.id);
                          const initialLimit = 3;
                          const showReadMore = subCatChips.length > initialLimit;
                          const visibleChips = isExpanded || !showReadMore ? subCatChips : subCatChips.slice(0, initialLimit);
                          const remainingCount = subCatChips.length - initialLimit;

                          return (
                            <>
                              {visibleChips.map(({ key, label }) => (
                                <Chip
                                  key={key}
                                  label={label}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.72rem',
                                    bgcolor: '#EFF6FF',
                                    color: '#1D4ED8',
                                    fontWeight: 600,
                                    borderRadius: '6px',
                                    border: '1px solid #DBEAFE',
                                  }}
                                />
                              ))}
                              {showReadMore && (
                                <Chip
                                  label={isExpanded ? 'Show Less' : `+${remainingCount} Read More`}
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleContactExpansion(contact.id);
                                  }}
                                  sx={{
                                    height: 22,
                                    fontSize: '0.72rem',
                                    bgcolor: isExpanded ? '#F1F5F9' : '#FFF7ED',
                                    color: isExpanded ? '#475569' : '#EA580C',
                                    fontWeight: 700,
                                    borderRadius: '6px',
                                    border: isExpanded ? '1px solid #E2E8F0' : '1px solid #FFEDD5',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      bgcolor: isExpanded ? '#E2E8F0' : '#FFEDD5',
                                    },
                                  }}
                                />
                              )}
                            </>
                          );
                        })()}
                      </Box>

                      {contact.description && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.82rem',
                            color: '#475569',
                            lineHeight: 1.5,
                            mb: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            zIndex: 1,
                          }}
                        >
                          {contact.description}
                        </Typography>
                      )}

                      {/* Location Box */}
                      {(() => {
                        const districtName =
                          contact.districtName ||
                          contact.district_Name ||
                          contact.districtTitle ||
                          (typeof contact.district === 'object' ? (contact.district?.name || contact.district?.title) : contact.district) ||
                          districts.find((d) => Number(d.id ?? d.districtId) === Number(contact.districtId))?.name ||
                          '';

                        const streetAndHouse = [contact.address, contact.houseNumber].filter(Boolean).join(' ').trim();
                        const districtAndPostal = [districtName, contact.postalCode].filter(Boolean).join(', ').trim();

                        if (!streetAndHouse && !districtAndPostal) return null;

                        return (
                          <Box
                            sx={{
                              mb: 2,
                              p: 1.5,
                              borderRadius: '12px',
                              bgcolor: '#F8FAFC',
                              border: '1px solid #E2E8F0',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1.25,
                              zIndex: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 30,
                                height: 30,
                                borderRadius: '8px',
                                bgcolor: 'rgba(255, 140, 66, 0.12)',
                                color: '#FF8C42',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                mt: 0.1,
                              }}
                            >
                              <LocationOn sx={{ fontSize: 18 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {streetAndHouse && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: '0.82rem',
                                    fontWeight: 700,
                                    color: '#0F172A',
                                    lineHeight: 1.35,
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {streetAndHouse}
                                </Typography>
                              )}
                              {districtAndPostal && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: '#64748B',
                                    display: 'block',
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {districtAndPostal}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      })()}

                      {/* Call to Action Row on Card */}
                      <Box
                        onClick={() => openCtaDialog(contact)}
                        sx={{
                          mb: 1.5,
                          p: 1.25,
                          borderRadius: '12px',
                          bgcolor: '#FFF5EB',
                          border: '1.5px solid #FF8C42',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          cursor: 'pointer',
                          zIndex: 1,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: '#E67E22',
                            bgcolor: '#FFEAD6',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                          <Campaign sx={{ color: '#FF8C42', fontSize: 18, flexShrink: 0 }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              color: '#D97706',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {contact.callToAction || t('pages.contacts.form.callToAction') || 'Call to Action'}
                          </Typography>
                        </Box>

                        <Tooltip title={t('common.edit') || 'Edit Call to Action'}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openCtaDialog(contact);
                            }}
                            sx={{
                              color: '#FF8C42',
                              p: 0.5,
                              '&:hover': { bgcolor: 'rgba(255, 140, 66, 0.15)' },
                            }}
                          >
                            <Edit sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {/* Booking Badges & Card Action Buttons */}
                      <Box sx={{ mt: 'auto', pt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, zIndex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <CalendarIcon sx={{ fontSize: 15, color: '#64748B' }} />
                          {contact.isOnlineBookingEnabled && (
                            <Chip
                              label="Online"
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                borderRadius: '999px',
                                bgcolor: '#EFF6FF',
                                color: '#2563EB',
                                border: '1px solid #BFDBFE',
                              }}
                            />
                          )}
                          {contact.isOfflineBookingEnabled && (
                            <Chip
                              label="Offline"
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                borderRadius: '999px',
                                bgcolor: '#F0FDF4',
                                color: '#16A34A',
                                border: '1px solid #BBF7D0',
                              }}
                            />
                          )}
                          {!contact.isOnlineBookingEnabled && !contact.isOfflineBookingEnabled && (
                            <Chip
                              label="None"
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                borderRadius: '999px',
                                bgcolor: '#F8FAFC',
                                color: '#64748B',
                                border: '1px solid #E2E8F0',
                              }}
                            />
                          )}
                        </Box>

                        <Box
                          className="card-actions"
                          sx={{ display: 'flex', flexDirection: 'row', gap: 0.75, alignItems: 'center', opacity: 1, transition: 'opacity 0.2s ease' }}
                        >
                          <Tooltip title={contact.isActive ? t('pages.bookingTemplates.deactivate') : t('pages.bookingTemplates.activate')}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleActiveStatus(contact)}
                              sx={{
                                width: 34,
                                height: 34,
                                bgcolor: contact.isActive ? 'rgba(46, 125, 50, 0.08)' : 'rgba(220, 53, 69, 0.08)',
                                border: '1px solid',
                                borderColor: contact.isActive ? 'rgba(46, 125, 50, 0.25)' : 'rgba(220, 53, 69, 0.25)',
                                color: contact.isActive ? '#2e7d32' : '#dc3545',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: contact.isActive ? '#2e7d32' : '#dc3545',
                                  color: '#fff',
                                  borderColor: contact.isActive ? '#2e7d32' : '#dc3545',
                                  transform: 'scale(1.08)',
                                },
                              }}
                            >
                              {contact.isActive ? <ToggleOnIcon sx={{ fontSize: 20 }} /> : <ToggleOffIcon sx={{ fontSize: 20 }} />}
                            </IconButton>
                          </Tooltip>
                          {/* Card View Button */}
                          <Tooltip title={t('pages.contacts.cardView') && t('pages.contacts.cardView') !== 'pages.contacts.cardView' ? t('pages.contacts.cardView') : 'Card View'}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenCardView(contact)}
                              sx={{
                                width: 34,
                                height: 34,
                                bgcolor: 'rgba(6, 57, 55, 0.08)',
                                border: '1px solid rgba(6, 57, 55, 0.25)',
                                color: '#063937',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: '#063937',
                                  color: '#fff',
                                  borderColor: '#063937',
                                  transform: 'scale(1.08)',
                                },
                              }}
                            >
                              <CardViewIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>

                          {/* Mobile View Button */}
                          <Tooltip title="Mobile View (Full App Preview)">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenPreview(contact)}
                              sx={{
                                width: 34,
                                height: 34,
                                bgcolor: 'rgba(25, 118, 210, 0.08)',
                                border: '1px solid rgba(25, 118, 210, 0.25)',
                                color: '#1976d2',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: '#1976d2',
                                  color: '#fff',
                                  borderColor: '#1976d2',
                                  transform: 'scale(1.08)',
                                },
                              }}
                            >
                              <SmartphoneIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('pages.contacts.tooltip.edit')}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpen(contact)}
                              sx={{
                                width: 34,
                                height: 34,
                                bgcolor: 'rgba(255, 140, 66, 0.08)',
                                border: '1px solid rgba(255, 140, 66, 0.25)',
                                color: ACCENT_COLOR,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: ACCENT_COLOR,
                                  color: '#fff',
                                  borderColor: ACCENT_COLOR,
                                  transform: 'scale(1.08)',
                                },
                              }}
                            >
                              <Edit sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>

                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
          </Grid>
        </ContactsListShell>
      </Box>

      {/* Contact Add/Edit/View Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: getContactDialogPaperSx(isMobile) }}
      >
        <DialogTitle sx={{
          pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#ffffff', color: '#000', px: 3, py: 2
        }}>
          <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, color: '#000000' }}>
            {selectedContact ? t('pages.contacts.dialog.editContact') : t('pages.contacts.dialog.addContact')}
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: 'rgba(0,0,0,0.54)' }}><CloseRounded /></IconButton>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, bgcolor: '#f8f9fa' }}>
          {isCreateWizard ? (
            <Stepper
              activeStep={createStep}
              alternativeLabel
              sx={{
                py: { xs: 1, sm: 2 },
                '& .MuiStepLabel-label': { fontSize: isMobile ? '0.65rem' : '0.78rem', fontWeight: 600 },
              }}
            >
              <Step>
                <StepLabel>{t('pages.contacts.tab.general')}</StepLabel>
              </Step>
              <Step>
                <StepLabel>{t('pages.contacts.tab.location')}</StepLabel>
              </Step>
              <Step>
                <StepLabel>{t('pages.contacts.tab.communication')}</StepLabel>
              </Step>
              <Step>
                <StepLabel>{t('pages.contacts.tab.business')}</StepLabel>
              </Step>
              <Step>
                <StepLabel>{t('pages.contacts.tab.info')}</StepLabel>
              </Step>
            </Stepper>
          ) : (
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: { xs: 48, sm: 64 }, fontSize: isMobile ? '0.8rem' : '0.95rem' } }}
            >
              <Tab icon={<InfoIcon />} label={t('pages.contacts.tab.general')} iconPosition="start" />
              <Tab icon={<MapIcon />} label={t('pages.contacts.tab.location')} iconPosition="start" />
              <Tab icon={<Phone />} label={t('pages.contacts.tab.communication')} iconPosition="start" />
              <Tab icon={<SettingsIcon />} label={t('pages.contacts.tab.business')} iconPosition="start" />
              <Tab icon={<DescriptionIcon />} label={t('pages.contacts.tab.info')} iconPosition="start" />
            </Tabs>
          )}
        </Box>

        <DialogContent
          sx={{
            px: { xs: 2, sm: 4 },
            pb: 4,
            pt: { xs: 3, sm: 5 },
            position: 'relative',
            '& .MuiFormLabel-asterisk': {
              color: '#d32f2f !important',
              fontWeight: 700,
            },
            '& .MuiInputLabel-asterisk': {
              color: '#d32f2f !important',
              fontWeight: 700,
            },
          }}
        >
          {contactDialogLoading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255,255,255,0.75)',
                zIndex: 3,
              }}
            >
              <CircularProgress sx={{ color: ACCENT_COLOR }} />
            </Box>
          )}
          <Grid container spacing={3} sx={{ pointerEvents: contactDialogLoading ? 'none' : 'auto' }}>
            {stepIndex === 0 && (
              <>
                <Grid item xs={12} sm={6} sx={{ pt: 0.5 }}>
                  <TextField
                    required
                    name="title"
                    label={t('pages.contacts.form.businessTitle')}
                    value={formData.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!formErrors.title}
                    helperText={formErrors.title}
                    fullWidth
                    disabled={viewMode || isSubmitting}
                    inputProps={{ maxLength: 50 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><BusinessIcon sx={{ color: 'primary.main' }} /></InputAdornment> }}
                    sx={{ '& .MuiOutlinedInput-root': { alignItems: 'center' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ pt: 0.5 }}>
                  <TextField
                    required
                    name="contactPerson"
                    label={t('pages.contacts.form.contactPerson')}
                    value={formData.contactPerson}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!formErrors.contactPerson}
                    helperText={formErrors.contactPerson}
                    fullWidth
                    disabled={viewMode || isSubmitting}
                    inputProps={{ maxLength: 50 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PeopleIcon sx={{ color: 'primary.main' }} /></InputAdornment> }}
                    sx={{ '& .MuiOutlinedInput-root': { alignItems: 'center' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    required
                    disabled={viewMode || isSubmitting || !effectiveServiceCategoryId}
                  >
                    <InputLabel id="subcategory-multiple-label" required error={!!formErrors.subCategoryId}>{t('pages.contacts.form.subcategories')}</InputLabel>
                    <Select
                      labelId="subcategory-multiple-label"
                      multiple
                      value={subCategoryIds}
                      onChange={handleSubCategoryChange}
                      onBlur={() => handleBlur({ target: { name: 'subCategoryId', value: subCategoryIds } })}
                      error={!!formErrors.subCategoryId}
                      onOpen={() => {
                        fetchSubCategories();
                      }}
                      input={(
                        <OutlinedInput
                          label={t('pages.contacts.form.subcategories')}
                        />
                      )}
                      renderValue={(selected) => {
                        if (selected && selected.length > 0) {
                          return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                              {selected.map((id) => {
                                const n = Number(id);
                                const sc = subCategories.find(
                                  (s) => Number(s.id ?? s.subCategoryId ?? s.SubCategoryId) === n
                                );
                                const label = sc ? getSubcategoryRowLabel(sc, n) : String(id);
                                return (
                                  <Chip
                                    key={String(id)}
                                    label={label}
                                    size="small"
                                    sx={{
                                      height: 24,
                                      fontSize: '0.72rem',
                                      fontWeight: 700,
                                      borderRadius: '999px',
                                      bgcolor: 'rgba(33, 150, 243, 0.12)',
                                      color: '#1976d2',
                                      border: '1px solid rgba(33, 150, 243, 0.35)',
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          );
                        }
                        return '';
                      }}
                    >
                      {subCategories.map((sc) => {
                        const id = Number(sc.id ?? sc.subCategoryId ?? sc.SubCategoryId);
                        if (!Number.isFinite(id) || id <= 0) return null;
                        const label = getSubcategoryRowLabel(sc, id);
                        const isChecked = subCategoryIds.some((sid) => Number(sid) === id);
                        return (
                          <MenuItem key={id} value={id}>
                            <Checkbox checked={isChecked} />
                            <ListItemText primary={label} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {(formErrors.subCategoryId || !effectiveServiceCategoryId || subCategories.length === 0) && (
                      <FormHelperText error={!!formErrors.subCategoryId}>
                        {formErrors.subCategoryId || (!effectiveServiceCategoryId
                          ? 'Sign in with an account that has a service category (contactType / categoryId) so subcategories can load.'
                          : 'No subcategories returned for your service category')}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField required name="description" label={t('pages.contacts.form.description')} value={formData.description} onChange={handleChange} onBlur={handleBlur} error={!!formErrors.description} helperText={formErrors.description || `${(formData.description || '').length}/150`} FormHelperTextProps={{ sx: { textAlign: 'right', width: '100%', m: 0, mt: 0.5 } }} inputProps={{ maxLength: 150 }} fullWidth multiline rows={4} disabled={viewMode || isSubmitting} />
                </Grid>
                <Grid item xs={12}>
                  <TextField required name="infoNote" label={t('pages.contacts.form.infoNote')} value={formData.infoNote} onChange={handleChange} onBlur={handleBlur} error={!!formErrors.infoNote} helperText={formErrors.infoNote || `${(formData.infoNote || '').length}/150`} FormHelperTextProps={{ sx: { textAlign: 'right', width: '100%', m: 0, mt: 0.5 } }} inputProps={{ maxLength: 150 }} fullWidth multiline rows={2} disabled={viewMode || isSubmitting} placeholder={t('pages.contacts.form.infoPlaceholder')}
                    InputProps={{ startAdornment: <InputAdornment position="start"><DescriptionIcon sx={{ color: 'primary.main' }} /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField required name="supportLanguages" label={t('pages.contacts.form.supportLanguages')} value={formData.supportLanguages} onChange={handleChange} onBlur={handleBlur} error={!!formErrors.supportLanguages} helperText={formErrors.supportLanguages} fullWidth disabled={viewMode || isSubmitting} placeholder={t('pages.contacts.form.supportLangPlaceholder')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="callToAction"
                    label={t('pages.contacts.form.callToAction') || 'Call to Action'}
                    value={formData.callToAction}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!formErrors.callToAction}
                    helperText={
                      <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{formErrors.callToAction || ''}</span>
                        <span>{`${(formData.callToAction || '').length}/30`}</span>
                      </Box>
                    }
                    fullWidth
                    disabled={viewMode || isSubmitting}
                    placeholder={t('pages.contacts.form.callToActionPlaceholder') || 'e.g. Click here, Register...'}
                    inputProps={{ maxLength: 30 }}
                  />
                </Grid>
              </>
            )}

            {stepIndex === 1 && (
              <>
                <Grid item xs={12}>
                  <Autocomplete
                    freeSolo
                    options={addressSuggestions}
                    loading={addressLoading}
                    noOptionsText="Not found address, please write correct address"
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.address?.label || '')}
                    value={formData.address || ''}
                    onInputChange={(event, newInputValue) => {
                      setFormData((prev) => ({ ...prev, address: newInputValue }));
                      handleAddressSearch(newInputValue);
                    }}
                    onChange={(event, newValue) => {
                      if (newValue && typeof newValue === 'object') {
                        const { address, position } = newValue;
                        setFormData((prev) => ({
                          ...prev,
                          address: address?.street || address?.label || prev.address,
                          houseNumber: address?.houseNumber || prev.houseNumber || '',
                          postalCode: address?.postalCode || prev.postalCode || '',
                          latitude: position?.lat != null ? String(position.lat) : prev.latitude,
                          longitude: position?.lng != null ? String(position.lng) : prev.longitude,
                        }));
                        // Clear errors for auto-filled fields
                        setFormErrors((prev) => ({
                          ...prev,
                          address: '',
                          houseNumber: address?.houseNumber ? '' : prev.houseNumber,
                          postalCode: address?.postalCode ? '' : prev.postalCode,
                          latitude: position?.lat != null ? '' : prev.latitude,
                          longitude: position?.lng != null ? '' : prev.longitude,
                        }));
                      }
                    }}
                    disabled={viewMode || isSubmitting}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        name="address"
                        label={t('pages.contacts.form.streetAddress')}
                        onBlur={handleBlur}
                        error={!!formErrors.address || (!addressLoading && (formData.address || '').length >= 3 && addressSuggestions.length === 0)}
                        helperText={getAddressFieldHelperText(
                          formErrors,
                          addressLoading,
                          formData,
                          viewMode,
                          addressSuggestions
                        )}
                        fullWidth
                        FormHelperTextProps={{
                          sx: {
                            color: getAddressFieldColor(formErrors, addressLoading, formData, addressSuggestions),
                            fontWeight: 600,
                          },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <LocationOn sx={{ color: 'primary.main' }} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {addressLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}><TextField required name="houseNumber" label={t('pages.contacts.form.houseNo')} value={formData.houseNumber} onChange={handleChange} onBlur={handleBlur} error={!!formErrors.houseNumber} helperText={formErrors.houseNumber} fullWidth disabled={viewMode || isSubmitting} /></Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required disabled={viewMode || isSubmitting || !formData.cityId} error={!!formErrors.districtId}>
                    <InputLabel required>{t('pages.contacts.form.district')}</InputLabel>
                    <Select
                      name="districtId"
                      value={formData.districtId}
                      label={t('pages.contacts.form.district')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <MenuItem value=""><em>{t('pages.contacts.form.selectDistrict')}</em></MenuItem>
                      {districts.map((d) => (
                        <MenuItem key={d.id} value={d.id}>
                          {d.name_EN || d.name || d.title_EN || `District ${d.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.districtId && <FormHelperText>{formErrors.districtId}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}><TextField required name="postalCode" label={t('pages.contacts.form.zip')} value={formData.postalCode} onChange={handleChange} onBlur={handleBlur} error={!!formErrors.postalCode} helperText={formErrors.postalCode} fullWidth disabled={viewMode || isSubmitting} placeholder={t('pages.contacts.form.zipPlaceholder')} /></Grid>
              </>
            )}

            {stepIndex === 2 && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField required name="email" label={t('pages.contacts.form.email')} value={formData.email} onChange={handleChange} onBlur={handleBlur} error={!!formErrors.email} helperText={formErrors.email} fullWidth disabled={viewMode || isSubmitting}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ color: 'primary.main' }} /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField required name="phone" label={t('pages.contacts.form.phone')} value={formData.phone} onChange={handleChange} onBlur={handleBlur} error={!!formErrors.phone} helperText={formErrors.phone} fullWidth disabled={viewMode || isSubmitting}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ color: 'primary.main' }} /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField required name="whatsapp" label={t('pages.contacts.form.whatsapp')} value={formData.whatsapp} onChange={handleChange} onBlur={handleBlur} error={!!formErrors.whatsapp} helperText={formErrors.whatsapp} fullWidth disabled={viewMode || isSubmitting}
                    InputProps={{ startAdornment: <InputAdornment position="start"><WhatsApp sx={{ color: '#25D366' }} /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField required name="website" label={t('pages.contacts.form.website')} value={formData.website} onChange={handleChange} onBlur={handleBlur} error={!!formErrors.website} helperText={formErrors.website} fullWidth disabled={viewMode || isSubmitting}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Language sx={{ color: 'primary.main' }} /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField name="whatsappText" label={t('pages.contacts.form.whatsappTemplate')} value={formData.whatsappText} onChange={handleChange} onBlur={handleBlur} error={!!formErrors.whatsappText} helperText={formErrors.whatsappText} inputProps={{ maxLength: 100 }} fullWidth multiline minRows={2} disabled={viewMode || isSubmitting}
                    InputProps={{ startAdornment: <InputAdornment position="start"><WhatsApp sx={{ color: '#25D366' }} /></InputAdornment> }}
                  />
                </Grid>
              </>
            )}

            {stepIndex === 3 && (
              <>
                <Grid item xs={12}>
                  <BusinessHoursScheduleField
                    value={formData.openingHours}
                    onChange={handleChange}
                    disabled={viewMode || isSubmitting}
                    error={!!formErrors.openingHours}
                    helperText={formErrors.openingHours}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 0.75, fontWeight: 700, color: '#29292b' }}>
                    {t('pages.contacts.form.logo')}
                  </Typography>
                  <Box
                    sx={{
                      border: `1px solid ${GRAY_BORDER}`,
                      borderRadius: 2,
                      p: 1.25,
                      bgcolor: 'transparent',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
                      {!viewMode && !logoFile && formData.logoPicture && (
                        <Box
                          onClick={() => openLogoLightbox(formData.logoPicture)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openLogoLightbox(formData.logoPicture);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          sx={{
                            position: 'relative',
                            width: 96,
                            height: 96,
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: `1px solid ${GRAY_BORDER}`,
                            bgcolor: '#fff',
                            flexShrink: 0,
                            boxShadow: IMG_SHADOW_THUMB,
                            cursor: 'zoom-in',
                            '&:focus-visible': { outline: `2px solid ${ACCENT_COLOR}`, outlineOffset: 2 },
                          }}
                        >
                          <Box
                            component="img"
                            src={formData.logoPicture}
                            alt=""
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
                          />
                          <IconButton
                            size="small"
                            aria-label={t('pages.contacts.form.removeSavedLogo')}
                            disabled={isSubmitting}
                            onClick={(e) => {
                              e.stopPropagation();
                              clearSavedLogo();
                            }}
                            sx={{
                              position: 'absolute',
                              top: 6,
                              right: 6,
                              width: 28,
                              height: 28,
                              p: 0,
                              bgcolor: 'rgba(255,255,255,0.95)',
                              boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                              color: 'text.secondary',
                              '&:hover': { bgcolor: '#fff' },
                            }}
                          >
                            <CloseRounded sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      )}
                      {!viewMode && logoFile && logoPreviewUrl && (
                        <Box
                          onClick={() => openLogoLightbox(logoPreviewUrl)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openLogoLightbox(logoPreviewUrl);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          sx={{
                            position: 'relative',
                            width: 96,
                            height: 96,
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: `1px solid ${GRAY_BORDER}`,
                            bgcolor: '#fff',
                            flexShrink: 0,
                            boxShadow: IMG_SHADOW_THUMB,
                            cursor: 'zoom-in',
                            '&:focus-visible': { outline: `2px solid ${ACCENT_COLOR}`, outlineOffset: 2 },
                          }}
                        >
                          <Box
                            component="img"
                            src={logoPreviewUrl}
                            alt=""
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
                          />
                          <IconButton
                            size="small"
                            aria-label={t('pages.contacts.form.removeLogo')}
                            disabled={isSubmitting}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeLogoFile();
                            }}
                            sx={{
                              position: 'absolute',
                              top: 6,
                              right: 6,
                              width: 28,
                              height: 28,
                              p: 0,
                              bgcolor: 'rgba(255,255,255,0.95)',
                              boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                              color: 'text.secondary',
                              '&:hover': { bgcolor: '#fff' },
                            }}
                          >
                            <CloseRounded sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      )}
                      {!viewMode && (
                        <Box
                          component="label"
                          sx={{
                            width: 96,
                            height: 96,
                            flexShrink: 0,
                            border: `2px dashed ${GRAY_BORDER}`,
                            borderRadius: 2,
                            cursor: isSubmitting ? 'default' : 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#fff',
                            boxShadow: IMG_SHADOW_THUMB,
                            transition: 'border-color 0.2s, background-color 0.2s',
                            '&:hover': getLogoUploadHoverSx(isSubmitting),
                          }}
                        >
                          <input type="file" hidden accept="image/png, image/jpeg, image/jpg, .png, .jpg, .jpeg" disabled={isSubmitting} onChange={handleLogoFileChange} />
                          <CloudUpload sx={{ fontSize: 32, color: 'text.secondary', mb: 0.25 }} />
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            {getLogoUploadCaption(logoFile, formData.logoPicture, t)}
                          </Typography>
                        </Box>
                      )}
                      {viewMode && (
                        <LogoViewModePreview
                          logoPicture={formData.logoPicture}
                          onOpenLightbox={openLogoLightbox}
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 0.75, fontWeight: 700, color: '#29292b' }}>
                    {t('pages.contacts.form.bannerImages')}
                  </Typography>
                  <Box
                    sx={{
                      border: `1px solid ${GRAY_BORDER}`,
                      borderRadius: 2,
                      p: 1.25,
                      bgcolor: 'transparent',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
                      {(Array.isArray(formData.picturesKeep) ? formData.picturesKeep : [])
                        .map((trimmedUrl, idx) => (
                          <Box
                            key={`saved-${idx}-${trimmedUrl.slice(-32)}`}
                            sx={{
                              position: 'relative',
                              width: 96,
                              height: 96,
                              borderRadius: 2,
                              overflow: 'hidden',
                              border: `1px solid ${GRAY_BORDER}`,
                              bgcolor: '#fff',
                              flexShrink: 0,
                              boxShadow: IMG_SHADOW_THUMB,
                            }}
                          >
                            <Box
                              component="img"
                              src={trimmedUrl}
                              alt=""
                              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                            {!viewMode && (
                              <IconButton
                                size="small"
                                aria-label={t('pages.contacts.form.removeSavedBanner')}
                                disabled={isSubmitting}
                                onClick={() => removeSavedBannerAt(idx)}
                                sx={{
                                  position: 'absolute',
                                  top: 6,
                                  right: 6,
                                  width: 28,
                                  height: 28,
                                  p: 0,
                                  bgcolor: 'rgba(255,255,255,0.95)',
                                  boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                                  color: 'text.secondary',
                                  '&:hover': { bgcolor: '#fff' },
                                }}
                              >
                                <CloseRounded sx={{ fontSize: 18 }} />
                              </IconButton>
                            )}
                          </Box>
                        ))}
                      {!viewMode &&
                        bannerFiles.map((file, idx) => {
                          const src = bannerPreviewUrls[idx];
                          if (!src) return null;
                          return (
                            <Box
                              key={`new-${idx}-${file.name}`}
                              sx={{
                                position: 'relative',
                                width: 96,
                                height: 96,
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: `1px solid ${GRAY_BORDER}`,
                                bgcolor: '#fff',
                                flexShrink: 0,
                                boxShadow: IMG_SHADOW_THUMB,
                              }}
                            >
                              <Box
                                component="img"
                                src={src}
                                alt=""
                                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                              />
                              <IconButton
                                size="small"
                                aria-label={t('pages.contacts.form.removeBanner')}
                                disabled={isSubmitting}
                                onClick={() => removeBannerFileAt(idx)}
                                sx={{
                                  position: 'absolute',
                                  top: 6,
                                  right: 6,
                                  width: 28,
                                  height: 28,
                                  p: 0,
                                  bgcolor: 'rgba(255,255,255,0.95)',
                                  boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                                  color: 'text.secondary',
                                  '&:hover': { bgcolor: '#fff' },
                                }}
                              >
                                <CloseRounded sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          );
                        })}
                      {!viewMode && (
                        <Box
                          component="label"
                          sx={{
                            width: 96,
                            height: 96,
                            flexShrink: 0,
                            border: `2px dashed ${GRAY_BORDER}`,
                            borderRadius: 2,
                            cursor: isSubmitting ? 'default' : 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#fff',
                            boxShadow: IMG_SHADOW_THUMB,
                            transition: 'border-color 0.2s, background-color 0.2s',
                            '&:hover': getLogoUploadHoverSx(isSubmitting),
                          }}
                        >
                          <input
                            type="file"
                            hidden
                            multiple
                            accept="image/png, image/jpeg, image/jpg, .png, .jpg, .jpeg"
                            disabled={isSubmitting}
                            onChange={handleBannerFilesChange}
                          />
                          <AddPhotoAlternate sx={{ fontSize: 36, color: 'text.secondary', mb: 0.25 }} />
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            {t('pages.contacts.form.addBanner')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {shouldShowEmptyBannerInViewMode(viewMode, formData.picturesKeep, bannerFiles.length) && (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <FormControlLabel
                          sx={{ ml: 0, mr: 2, width: 'fit-content' }}
                          control={(
                            <Switch
                              checked={Boolean(formData.isActive)}
                              onChange={(e) => setFormData((p) => ({ ...p, isActive: e.target.checked }))}
                              disabled={viewMode || isSubmitting || contactDialogLoading}
                            />
                          )}
                          label={t('common.active')}
                          labelPlacement="start"
                        />
                        <FormControlLabel
                          sx={{ ml: 0, mr: 2, width: 'fit-content' }}
                          control={(
                            <Switch
                              checked={Boolean(formData.isBookingAppointmentEnabled)}
                              onChange={(e) =>
                                setFormData((p) => ({ ...p, isBookingAppointmentEnabled: e.target.checked }))
                              }
                              disabled={viewMode || isSubmitting || contactDialogLoading}
                            />
                          )}
                          label="Booking Available"
                          labelPlacement="start"
                        />
                        <FormControlLabel
                          sx={{ ml: 0, mr: 2, width: 'fit-content' }}
                          control={(
                            <Switch
                              checked={Boolean(formData.isChatEnabled)}
                              onChange={(e) =>
                                setFormData((p) => ({ ...p, isChatEnabled: e.target.checked }))
                              }
                              disabled={viewMode || isSubmitting || contactDialogLoading}
                            />
                          )}
                          label={t('pages.contacts.form.isChatEnabled') || 'Chat Support'}
                          labelPlacement="start"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <FormControlLabel
                          sx={{ ml: 0, mr: 2, width: 'fit-content' }}
                          control={(
                            <Switch
                              checked={Boolean(formData.isOnlineBookingEnabled)}
                              onChange={(e) =>
                                setFormData((p) => ({ ...p, isOnlineBookingEnabled: e.target.checked }))
                              }
                              disabled={viewMode || isSubmitting || contactDialogLoading}
                            />
                          )}
                          label="Online Booking"
                          labelPlacement="start"
                        />
                        <FormControlLabel
                          sx={{ ml: 0, mr: 2, width: 'fit-content' }}
                          control={(
                            <Switch
                              checked={Boolean(formData.isOfflineBookingEnabled)}
                              onChange={(e) =>
                                setFormData((p) => ({ ...p, isOfflineBookingEnabled: e.target.checked }))
                              }
                              disabled={viewMode || isSubmitting || contactDialogLoading}
                            />
                          )}
                          label="Offline Booking"
                          labelPlacement="start"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}

            {stepIndex === 4 && (
              <>
                <Grid item xs={12} sm={6}>
                  <StringArrayField
                    label={t('pages.contacts.form.documentsRequired')}
                    placeholder={t('pages.contacts.form.documentsRequiredPlaceholder')}
                    items={formData.documentsRequired}
                    onChange={(items) => setFormData((prev) => ({ ...prev, documentsRequired: items }))}
                    disabled={viewMode || isSubmitting}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StringArrayField
                    label={t('pages.contacts.form.rulesAndRegulations')}
                    placeholder={t('pages.contacts.form.rulesAndRegulationsPlaceholder')}
                    items={formData.rulesAndRegulations}
                    onChange={(items) => setFormData((prev) => ({ ...prev, rulesAndRegulations: items }))}
                    disabled={viewMode || isSubmitting}
                  />
                </Grid>
                <Grid item xs={12}>
                  <PricingInformationField
                    label={t('pages.contacts.form.pricingInformation')}
                    items={formData.pricingInformation}
                    onChange={(items) => setFormData((prev) => ({ ...prev, pricingInformation: items }))}
                    disabled={viewMode || isSubmitting}
                    t={t}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            p: { xs: 2, sm: 3 },
            bgcolor: '#f8f9fa',
            borderTop: '1px solid',
            borderColor: 'divider',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Button onClick={handleClose} variant="outlined" fullWidth={isMobile} sx={{ borderRadius: '10px', px: 3 }}>
            {t('common.cancel')}
          </Button>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'flex-end', flex: isMobile ? '1 1 100%' : '1 1 auto' }}>
            {isCreateWizard && createStep > 0 && (
              <Button
                onClick={handleWizardPrevious}
                variant="outlined"
                sx={{ borderRadius: '10px', px: 3, textTransform: 'none', fontWeight: 600 }}
              >
                {t('pages.contacts.wizard.previous')}
              </Button>
            )}
            {isCreateWizard && createStep < 4 && (
              <Button
                onClick={handleWizardNext}
                variant="contained"
                sx={{ borderRadius: '10px', px: 4, background: '#FF8C42', '&:hover': { background: '#E06526' }, textTransform: 'none', fontWeight: 700 }}
              >
                {t('pages.contacts.wizard.next')}
              </Button>
            )}
            {isCreateWizard && createStep === 4 && (
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={isSubmitting || contactDialogLoading}
                sx={{ borderRadius: '10px', px: 4, background: '#FF8C42', '&:hover': { background: '#E06526' }, textTransform: 'none', fontWeight: 700 }}
              >
                {isSubmitting ? t('pages.contacts.dialog.adding') : t('pages.contacts.wizard.createContact')}
              </Button>
            )}

            {!isCreateWizard && (
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={isSubmitting || contactDialogLoading}
                sx={{ borderRadius: '10px', px: 4, background: '#FF8C42', '&:hover': { background: '#E06526' } }}
              >
                {getEditDialogSubmitLabel(isSubmitting, selectedContact, t)}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={logoLightboxOpen}
        onClose={closeLogoLightbox}
        maxWidth="lg"
        fullWidth
        elevation={0}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.38)',
            },
          },
        }}
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            backgroundImage: 'none',
            overflow: 'visible',
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            bgcolor: 'transparent',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {logoLightboxSrc && (
            <Box
              sx={{
                position: 'relative',
                display: 'inline-block',
                maxWidth: '100%',
                lineHeight: 0,
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: IMG_SHADOW_LIGHTBOX,
              }}
            >
              <IconButton
                aria-label={t('pages.contacts.dialog.close')}
                onClick={closeLogoLightbox}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  color: 'text.primary',
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.28)',
                  '&:hover': { bgcolor: '#fff' },
                }}
              >
                <CloseRounded sx={{ fontSize: 20 }} />
              </IconButton>
              <Box
                component="img"
                src={logoLightboxSrc}
                alt=""
                sx={{
                  maxWidth: '100%',
                  maxHeight: 'min(85vh, 900px)',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                  verticalAlign: 'top',
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Mobile Mockup Preview Dialog (App-Side Real Mobile UI) */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }
        }}
      >
        <IconButton
          onClick={() => setPreviewDialogOpen(false)}
          sx={{
            position: 'absolute',
            top: -25,
            right: 0,
            color: '#fff',
            bgcolor: 'rgba(0,0,0,0.5)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
          }}
        >
          <CloseRounded />
        </IconButton>

        <Box
          sx={{
            width: 330,
            height: 700,
            borderRadius: '40px',
            border: '12px solid #1e293b',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
            bgcolor: '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Top Status Bar Mockup */}
          <Box
            sx={{
              height: 28,
              bgcolor: '#063937',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              pt: 0.5,
              fontSize: '0.72rem',
              fontWeight: 700,
              zIndex: 10,
              flexShrink: 0,
            }}
          >
            <span>3:07</span>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, fontSize: '0.65rem' }}>
              <span>5G</span>
              <span>📶</span>
              <span>45% 🔋</span>
            </Box>
          </Box>

          {/* App Header / Navigation Bar */}
          <Box
            sx={{
              height: 48,
              bgcolor: '#063937',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1.5,
              zIndex: 10,
              flexShrink: 0,
              borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <IconButton size="small" onClick={() => setPreviewDialogOpen(false)} sx={{ color: '#fff', p: 0.5 }}>
              <ChevronLeftIcon sx={{ fontSize: 26 }} />
            </IconButton>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.05rem',
                color: '#FFFFFF',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 220,
                textAlign: 'center',
              }}
            >
              {previewData.title || "Business Preview"}
            </Typography>
            <IconButton size="small" sx={{ color: '#fff', p: 0.5 }}>
              <ShareIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          {/* Scrollable Mobile Body */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              bgcolor: '#ffffff',
              pb: '165px',
              position: 'relative',
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            {/* Banner Carousel */}
            {(() => {
              const carouselImages = [];
              if (previewData.logoUrl) carouselImages.push(previewData.logoUrl);
              if (previewData.bannerUrls && previewData.bannerUrls.length > 0) {
                previewData.bannerUrls.forEach(url => {
                  if (url !== previewData.logoUrl) carouselImages.push(url);
                });
              }
              const displayImages = carouselImages.length > 0 ? carouselImages : [
                'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&auto=format&fit=crop'
              ];

              return (
                <Box sx={{ position: 'relative', height: 210, bgcolor: '#e2e8f0', overflow: 'hidden' }}>
                  <Box
                    ref={bannerScrollRef}
                    onScroll={(e) => {
                      const scrollLeft = e.target.scrollLeft;
                      const width = e.target.clientWidth;
                      if (width > 0) {
                        const idx = Math.round(scrollLeft / width);
                        if (idx !== activeBannerIndex) setActiveBannerIndex(idx);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      overflowX: 'auto',
                      width: '100%',
                      height: '100%',
                      scrollSnapType: 'x mandatory',
                      '&::-webkit-scrollbar': { display: 'none' },
                      msOverflowStyle: 'none',
                      scrollbarWidth: 'none',
                    }}
                  >
                    {displayImages.map((url, i) => (
                      <Box
                        key={i}
                        component="img"
                        src={url}
                        alt={`Banner ${i}`}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          flexShrink: 0,
                          scrollSnapAlign: 'start',
                        }}
                      />
                    ))}
                  </Box>

                  {/* 1/2 Slide Badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 22,
                      right: 14,
                      bgcolor: 'rgba(0,0,0,0.65)',
                      color: '#ffffff',
                      px: 1.2,
                      py: 0.35,
                      borderRadius: '12px',
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      zIndex: 3,
                    }}
                  >
                    {activeBannerIndex + 1}/{displayImages.length}
                  </Box>

                  {/* Slider Indicator Dots */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 22,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.6,
                      zIndex: 3,
                    }}
                  >
                    {displayImages.map((_, dotIdx) => (
                      <Box
                        key={dotIdx}
                        sx={{
                          height: 6,
                          width: dotIdx === activeBannerIndex ? 18 : 6,
                          borderRadius: 3,
                          bgcolor: dotIdx === activeBannerIndex ? '#FF6B6B' : 'rgba(255,255,255,0.75)',
                          transition: 'all 0.2s ease',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })()}

            {/* Overlapping White Sheet */}
            <Box
              sx={{
                mt: -3,
                borderTopLeftRadius: '24px',
                borderTopRightRadius: '24px',
                bgcolor: '#ffffff',
                px: 2,
                pt: 2.2,
                position: 'relative',
                zIndex: 4,
              }}
            >
              {/* Row 1: VERIFIED badge on left, Distance on right */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
                {previewData.isVerified ? (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      bgcolor: '#D1FAE5',
                      color: '#059669',
                      px: 1.1,
                      py: 0.25,
                      borderRadius: '999px',
                      fontSize: '0.68rem',
                      fontWeight: 750,
                      letterSpacing: '0.03em',
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 14, color: '#059669' }} />
                    VERIFIED
                  </Box>
                ) : (
                  <Box />
                )}
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 750, color: '#0F172A' }}>
                  {previewData.distance || '25.9 km'}
                </Typography>
              </Box>

              {/* Row 2: Title + Green Verified Badge (Matches Image Exactly) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.75 }}>
                <Typography
                  sx={{
                    fontWeight: 850,
                    fontSize: '1.12rem',
                    color: '#063937',
                    lineHeight: 1.2,
                  }}
                >
                  {previewData.title}
                </Typography>
                {previewData.isVerified && (
                  <VerifiedBadgeIcon sx={{ color: '#2EB584', fontSize: 22, flexShrink: 0 }} />
                )}
              </Box>

              {/* Row 3: Quick Action Buttons (Enclosed in a single outer rounded card) */}
              <Box
                sx={{
                  border: '1px solid #E2E8F0',
                  borderRadius: '20px',
                  bgcolor: '#FFFFFF',
                  p: 1.25,
                  py: 1.5,
                  mb: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 0.5,
                }}
              >
                {/* Book Now */}
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: '13px',
                      bgcolor: '#EDF2F4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#063937',
                      transition: 'all 0.15s ease',
                      '&:hover': { bgcolor: '#E2E8F0' },
                    }}
                  >
                    <CalendarIcon sx={{ fontSize: 19 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.67rem',
                      fontWeight: 650,
                      color: '#0F172A',
                      mt: 0.6,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Book Now
                  </Typography>
                </Box>

                {/* Call */}
                <Box
                  onClick={() => {
                    if (previewData.phone) window.open(`tel:${previewData.phone}`);
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: '13px',
                      bgcolor: '#EDF2F4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#063937',
                      transition: 'all 0.15s ease',
                      '&:hover': { bgcolor: '#E2E8F0' },
                    }}
                  >
                    <Phone sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.67rem',
                      fontWeight: 650,
                      color: '#0F172A',
                      mt: 0.6,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Call
                  </Typography>
                </Box>

                {/* Website */}
                <Box
                  onClick={() => {
                    if (previewData.website) {
                      const url = previewData.website.startsWith('http') ? previewData.website : `https://${previewData.website}`;
                      window.open(url, '_blank');
                    }
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: '13px',
                      bgcolor: '#EDF2F4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#063937',
                      transition: 'all 0.15s ease',
                      '&:hover': { bgcolor: '#E2E8F0' },
                    }}
                  >
                    <Language sx={{ fontSize: 19 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.67rem',
                      fontWeight: 650,
                      color: '#0F172A',
                      mt: 0.6,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Website
                  </Typography>
                </Box>

                {/* Email */}
                <Box
                  onClick={() => {
                    if (previewData.email) window.open(`mailto:${previewData.email}`);
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: '13px',
                      bgcolor: '#EDF2F4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#063937',
                      transition: 'all 0.15s ease',
                      '&:hover': { bgcolor: '#E2E8F0' },
                    }}
                  >
                    <Email sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.67rem',
                      fontWeight: 650,
                      color: '#0F172A',
                      mt: 0.6,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Email
                  </Typography>
                </Box>

                {/* Directions */}
                <Box
                  onClick={() => {
                    if (previewData.address) {
                      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(previewData.address)}`, '_blank');
                    }
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: '13px',
                      bgcolor: '#EDF2F4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#063937',
                      transition: 'all 0.15s ease',
                      '&:hover': { bgcolor: '#E2E8F0' },
                    }}
                  >
                    <DirectionsIcon sx={{ fontSize: 19 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.67rem',
                      fontWeight: 650,
                      color: '#0F172A',
                      mt: 0.6,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Directions
                  </Typography>
                </Box>
              </Box>

              {/* Row 4: Address */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 1 }}>
                <LocationOn sx={{ color: '#EF4444', fontSize: 18, mt: 0.15, flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.74rem', color: '#475569', lineHeight: 1.4, fontWeight: 500 }}>
                  {previewData.address}
                </Typography>
              </Box>

              {/* Row 5: District Pill */}
              {previewData.districtName && (
                <Box sx={{ mb: 1.25 }}>
                  <Box
                    sx={{
                      display: 'inline-block',
                      bgcolor: '#EDF5F1',
                      color: '#063937',
                      borderRadius: '10px',
                      px: 1.5,
                      py: 0.35,
                      fontSize: '0.72rem',
                      fontWeight: 700,
                    }}
                  >
                    {previewData.districtName}
                  </Box>
                </Box>
              )}

              {/* Row 6: Call To Action Pill */}
              {previewData.callToAction && (
                <Box sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.6,
                      bgcolor: '#FFF3E0',
                      border: '1px solid #FFCC80',
                      color: '#E65100',
                      borderRadius: '10px',
                      px: 1.4,
                      py: 0.45,
                      fontSize: '0.73rem',
                      fontWeight: 700,
                    }}
                  >
                    <Campaign sx={{ fontSize: 16, color: '#E65100' }} />
                    {previewData.callToAction}
                  </Box>
                </Box>
              )}

              {/* Row 7: Info Note Box */}
              {previewData.infoNote && (
                <Box
                  sx={{
                    bgcolor: '#F0F9FF',
                    borderLeft: '4px solid #063937',
                    borderRadius: '8px',
                    p: 1.5,
                    mb: 1.75,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <InfoIcon sx={{ color: '#063937', fontSize: 18, mt: 0.15, flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: '0.73rem',
                          color: '#0F172A',
                          lineHeight: 1.4,
                          display: infoNoteExpanded ? 'block' : '-webkit-box',
                          WebkitLineClamp: infoNoteExpanded ? 'unset' : 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {previewData.infoNote}
                      </Typography>
                      {previewData.infoNote.length > 70 && (
                        <Typography
                          onClick={() => setInfoNoteExpanded(!infoNoteExpanded)}
                          sx={{
                            fontSize: '0.7rem',
                            fontWeight: 750,
                            color: '#063937',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            mt: 0.5,
                            display: 'inline-block',
                          }}
                        >
                          {infoNoteExpanded ? 'Show less' : 'Read more'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Ratings Row (Dual pills - Exact Match to Screenshot) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: '#FFFDF0',
                    border: '1px solid #FDE68A',
                    borderRadius: '999px',
                    px: 1.2,
                    py: 0.35,
                  }}
                >
                  <svg width="11" height="12" viewBox="0 0 18 20" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M3 18V9C3 5.686 5.686 3 9 3C12.314 3 15 5.686 15 9V18" stroke="#063937" strokeWidth="3.5" strokeLinecap="round"/>
                    <circle cx="16" cy="16" r="2.2" fill="#EF4444"/>
                  </svg>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#D97706', lineHeight: 1 }}>
                    {previewData.ratingInfo && previewData.ratingInfo.hasRating ? previewData.ratingInfo.averageRating.toFixed(1) : '4.0'}
                  </Typography>
                  <StarIcon sx={{ fontSize: 11, color: '#F59E0B' }} />
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 500, color: '#64748B', lineHeight: 1 }}>
                    ({previewData.ratingInfo && previewData.ratingInfo.hasRating ? (previewData.ratingInfo.totalRating || 1) : 1} review)
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: '#F0F9FF',
                    border: '1px solid #BAE6FD',
                    borderRadius: '999px',
                    px: 1.2,
                    py: 0.35,
                  }}
                >
                  <svg width="10" height="13" viewBox="0 0 24 32" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 9.25 12 20 12 20s12-10.75 12-20c0-6.627-5.373-12-12-12z" fill="#EA4335"/>
                    <circle cx="12" cy="11" r="4.5" fill="#FFFFFF"/>
                  </svg>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#0284C7', lineHeight: 1 }}>
                    4.5
                  </Typography>
                  <StarIcon sx={{ fontSize: 11, color: '#0284C7' }} />
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 500, color: '#64748B', lineHeight: 1 }}>
                    (120 reviews)
                  </Typography>
                </Box>
              </Box>

              {/* About Section */}
              {previewData.description && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.2 }}>
                    <Box sx={{ width: 3.5, height: 16, bgcolor: '#063937', borderRadius: '2px' }} />
                    <Typography sx={{ fontWeight: 850, fontSize: '0.88rem', color: '#0F172A' }}>
                      About
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.74rem', color: '#475569', lineHeight: 1.5 }}>
                    {previewData.description}
                  </Typography>
                </Box>
              )}

              {/* Services & Info Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                  <Box sx={{ width: 3.5, height: 16, bgcolor: '#063937', borderRadius: '2px' }} />
                  <Typography sx={{ fontWeight: 850, fontSize: '0.88rem', color: '#0F172A' }}>
                    Services & Info
                  </Typography>
                </Box>

                {/* SUPPORT AVAILABLE IN */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '6px',
                        bgcolor: '#FFF1F2',
                        color: '#E11D48',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: 800,
                      }}
                    >
                      文
                    </Box>
                    <Typography sx={{ fontSize: '0.67rem', fontWeight: 750, color: '#475569', letterSpacing: '0.04em' }}>
                      SUPPORT AVAILABLE IN
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {(previewData.supportLanguages ? previewData.supportLanguages.split(',') : ['English']).map((lang, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          bgcolor: '#FFFFFF',
                          border: '1px solid #E2E8F0',
                          borderRadius: '12px',
                          px: 1.4,
                          py: 0.6,
                          fontSize: '0.74rem',
                          fontWeight: 650,
                          color: '#0F172A',
                        }}
                      >
                        {lang.trim()}
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Green Tinted Protection Plan Card */}
                {(() => {
                  const plansList = previewData.pricingInformation?.plans || [];
                  const displayPlans = plansList.length > 0 ? plansList : [
                    {
                      title: 'test',
                      subtitle: 'ghvjh',
                      badge: 'MOST POPULAR',
                      price: '25.23',
                      period: 'day',
                      name: 'cv',
                      description: 'sdf',
                      highlights: ['sdf', 'fgbgf', 'jyuj'],
                      hint: 'df',
                    }
                  ];

                  return displayPlans.map((plan, pIdx) => (
                    <Box
                      key={pIdx}
                      sx={{
                        bgcolor: '#F0FDF4',
                        border: '1.5px solid #DCFCE7',
                        borderRadius: '20px',
                        p: 2,
                        mb: 2.5,
                      }}
                    >
                      {/* Plan Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            bgcolor: '#063937',
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#FFFFFF',
                            flexShrink: 0,
                          }}
                        >
                          <ShieldIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.84rem', color: '#0F172A', lineHeight: 1.2 }}>
                            {plan.title || previewData.callToAction || 'Protection Plan'}
                          </Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontWeight: 500 }}>
                            {plan.subtitle || 'Customized for expats & students'}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Inner White Box */}
                      <Box
                        sx={{
                          bgcolor: '#FFFFFF',
                          border: '1px solid #E2E8F0',
                          borderRadius: '14px',
                          p: 1.75,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Box
                            sx={{
                              bgcolor: '#F1F5F9',
                              color: '#0F172A',
                              borderRadius: '6px',
                              px: 1,
                              py: 0.3,
                              fontSize: '0.62rem',
                              fontWeight: 800,
                              letterSpacing: '0.04em',
                            }}
                          >
                            {plan.badge || 'MOST POPULAR'}
                          </Box>
                          <Typography sx={{ fontSize: '0.86rem', fontWeight: 800, color: '#0F172A' }}>
                            {plan.price || '25.23'} <span style={{ fontSize: '0.78rem', fontWeight: 500, color: '#64748B' }}>/ {plan.period || 'day'}</span>
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.75 }}>
                          <Typography sx={{ fontSize: '0.86rem', fontWeight: 750, color: '#0F172A' }}>
                            {plan.name || plan.title || 'Basic Cover'}
                          </Typography>
                          <Box
                            onClick={() => setPlanExpanded(!planExpanded)}
                            sx={{
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.25,
                              fontSize: '0.78rem',
                              fontWeight: 700,
                              color: '#0F172A',
                            }}
                          >
                            {planExpanded ? 'Hide' : 'Details'}
                            {planExpanded ? <ArrowUpIcon sx={{ fontSize: 16 }} /> : <ArrowDownIcon sx={{ fontSize: 16 }} />}
                          </Box>
                        </Box>

                        {/* Expanded details */}
                        {planExpanded && (
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #F1F5F9' }}>
                            {plan.description && (
                              <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mb: 1.25 }}>
                                {plan.description}
                              </Typography>
                            )}
                            {(plan.highlights || ['Coverage in Germany & EU', 'Instant Digital Policy', 'No paperwork required']).map((item, idx) => (
                              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                                <CheckCircle sx={{ fontSize: 16, color: '#063937' }} />
                                <Typography sx={{ fontSize: '0.73rem', fontWeight: 600, color: '#0F172A' }}>
                                  {item}
                                </Typography>
                              </Box>
                            ))}
                            {plan.hint && (
                              <Box
                                sx={{
                                  bgcolor: '#FFFBEB',
                                  border: '1px solid #FEF3C7',
                                  borderRadius: '8px',
                                  p: 1,
                                  mt: 1.25,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                }}
                              >
                                <InfoIcon sx={{ fontSize: 14, color: '#D97706' }} />
                                <Typography sx={{ fontSize: '0.7rem', color: '#B45309', fontWeight: 600 }}>
                                  {plan.hint}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ));
                })()}

                {/* WHAT WE OFFER (Categories) */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '6px',
                        bgcolor: '#FFF1F2',
                        color: '#E11D48',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AppsIcon sx={{ fontSize: 13 }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.67rem', fontWeight: 750, color: '#475569', letterSpacing: '0.04em' }}>
                      WHAT WE OFFER
                    </Typography>
                  </Box>

                  {(() => {
                    const catList = previewData.subCategoryNames || [
                      'Health Insurance', 'Life Insurance', 'Property Insurance', 'Liability Insurance', 'Cooperative Banks', 'Savings Banks', 'Online Banking', 'Tax Consultation', 'Legal Advisory'
                    ];
                    const visibleCats = categoriesExpanded ? catList : catList.slice(0, 2);
                    const remainingCount = catList.length - 2;

                    return (
                      <Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                          {visibleCats.map((cat, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                bgcolor: '#FFFFFF',
                                border: '1px solid #E2E8F0',
                                borderRadius: '12px',
                                px: 1.4,
                                py: 0.7,
                                fontSize: '0.74rem',
                                fontWeight: 650,
                                color: '#0F172A',
                              }}
                            >
                              {cat}
                            </Box>
                          ))}
                        </Box>
                        {remainingCount > 0 && (
                          <Box
                            onClick={() => setCategoriesExpanded(!categoriesExpanded)}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              bgcolor: '#FFF1F2',
                              border: '1px solid #FECDD3',
                              color: '#E11D48',
                              borderRadius: '999px',
                              px: 1.6,
                              py: 0.5,
                              fontSize: '0.72rem',
                              fontWeight: 750,
                              cursor: 'pointer',
                              mt: 0.5,
                            }}
                          >
                            {categoriesExpanded ? 'Show Less ⌃' : `+ ${remainingCount} More ⌵`}
                          </Box>
                        )}
                      </Box>
                    );
                  })()}
                </Box>

                {/* Opening Hours Widget */}
                <Box
                  sx={{
                    bgcolor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '16px',
                    p: 2,
                    mb: 3,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        bgcolor: '#DCFCE7',
                        color: '#16A34A',
                        borderRadius: '999px',
                        px: 1.4,
                        py: 0.4,
                        fontSize: '0.75rem',
                        fontWeight: 800,
                      }}
                    >
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#16A34A' }} />
                      Open
                    </Box>
                    <Typography sx={{ fontSize: '0.73rem', fontWeight: 600, color: '#64748B' }}>
                      Closes at 06:00 PM
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#EF4444' }} />
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#0F172A' }}>
                        Wednesday
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#0F172A' }}>
                      09:00 AM - 06:00 PM
                    </Typography>
                  </Box>

                  {hoursExpanded && (
                    <Box sx={{ pt: 1.5, pb: 1, borderTop: '1px solid #F1F5F9', display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {DAYS_CONFIG.map(({ key, fullLabel }) => {
                        const sched = parseScheduleFromOpeningHours(previewData.openingHours);
                        const item = sched[key] || { open: true, sessions: [{ open: '09:00', close: '18:00' }] };
                        return (
                          <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                            <Typography sx={{ fontSize: '0.78rem', color: '#475569', fontWeight: 600 }}>{fullLabel}</Typography>
                            <Typography sx={{ fontSize: '0.78rem', color: '#0F172A', fontWeight: 700 }}>
                              {item.open && item.sessions?.length > 0 ? `${formatTimeTo12Hour(item.sessions[0].open)} - ${formatTimeTo12Hour(item.sessions[0].close)}` : 'Closed'}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  <Box
                    onClick={() => setHoursExpanded(!hoursExpanded)}
                    sx={{
                      pt: 1,
                      textAlign: 'center',
                      borderTop: '1px solid #F1F5F9',
                      cursor: 'pointer',
                      fontSize: '0.74rem',
                      fontWeight: 700,
                      color: '#0F172A',
                    }}
                  >
                    {hoursExpanded ? 'Hide opening hours ⌃' : 'Show opening hours ⌵'}
                  </Box>
                </Box>

                {/* Required Documents Section */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                    <Box sx={{ width: 22, height: 22, borderRadius: '6px', bgcolor: '#FFF1F2', color: '#E11D48', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FileCopyIcon sx={{ fontSize: 13 }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.67rem', fontWeight: 750, color: '#475569', letterSpacing: '0.04em' }}>
                      REQUIRED DOCUMENTS
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {(previewData.documentsRequired?.length > 0 ? previewData.documentsRequired : [
                      'Only the mandatory special drives (Sonderfahrten) are compulsory; the required number depends on the license class',
                      'generally aligned with the end of training; in some cases wait times of up to 2 weeks may occur'
                    ]).map((doc, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box component="span" sx={{ fontSize: '1.1rem', lineHeight: 1, color: '#0F172A' }}>•</Box>
                        <Typography sx={{ fontSize: '0.73rem', color: '#475569', lineHeight: 1.45 }}>
                          {doc}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Terms & Conditions Section */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                    <Box sx={{ width: 22, height: 22, borderRadius: '6px', bgcolor: '#FFF1F2', color: '#E11D48', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <GavelIcon sx={{ fontSize: 13 }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.67rem', fontWeight: 750, color: '#475569', letterSpacing: '0.04em' }}>
                      TERMS & CONDITIONS
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {(previewData.rulesAndRegulations?.length > 0 ? previewData.rulesAndRegulations : [
                      'Lessons canceled later than 2 business days before the appointment are charged at 75% of the lesson fee',
                      'Theory learning supported via both a mobile app and an online website tool to track progress'
                    ]).map((rule, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box component="span" sx={{ fontSize: '1.1rem', lineHeight: 1, color: '#0F172A' }}>•</Box>
                        <Typography sx={{ fontSize: '0.73rem', color: '#475569', lineHeight: 1.45 }}>
                          {rule}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Location Section */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                    <Box sx={{ width: 3.5, height: 16, bgcolor: '#063937', borderRadius: '2px' }} />
                    <Typography sx={{ fontWeight: 850, fontSize: '0.88rem', color: '#0F172A' }}>
                      Location
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.74rem', color: '#475569', lineHeight: 1.4, mb: 1.25 }}>
                    {previewData.address}
                  </Typography>

                  {/* Map Mockup */}
                  <Box
                    sx={{
                      position: 'relative',
                      height: 140,
                      borderRadius: '16px',
                      overflow: 'hidden',
                      bgcolor: '#E8F5E9',
                      backgroundImage: 'radial-gradient(#C8E6C9 2px, transparent 2px), radial-gradient(#C8E6C9 2px, #E8F5E9 2px)',
                      backgroundSize: '15px 15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <LocationOn sx={{ color: '#EF4444', fontSize: 36, zIndex: 2, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))' }} />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 10,
                        right: 10,
                        bgcolor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        borderRadius: '999px',
                        px: 1.6,
                        py: 0.6,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        zIndex: 2,
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        if (previewData.address) {
                          window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(previewData.address)}`, '_blank');
                        }
                      }}
                    >
                      <DirectionsIcon sx={{ fontSize: 14, color: '#063937' }} />
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#063937' }}>Navigate</Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Rate Your Experience */}
                <Box
                  sx={{
                    bgcolor: '#FFFDF0',
                    border: '1.5px solid #FEF08A',
                    borderRadius: '18px',
                    p: 1.75,
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: '50%',
                      bgcolor: '#FEF9C3',
                      color: '#D97706',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <StarIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 750, color: '#0F172A', lineHeight: 1.2 }}>
                      Rate Your Experience
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: '#64748B', mt: 0.25 }}>
                      Your rating and review help others in the community.
                    </Typography>
                  </Box>
                  <ChevronRightIcon sx={{ color: '#D97706', fontSize: 20 }} />
                </Box>

                {/* Report Contact */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.6,
                    color: '#64748B',
                    cursor: 'pointer',
                    mb: 2,
                  }}
                >
                  <FlagIcon sx={{ fontSize: 16 }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
                    Report Contact
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Sticky Floating Partner Booking & Bottom Fixed Action Row */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 1.5,
              bgcolor: '#FFFFFF',
              borderTop: '1px solid #F1F5F9',
              boxShadow: '0 -4px 20px rgba(0,0,0,0.06)',
              zIndex: 20,
            }}
          >
            {/* Book via partner banner */}
            <Box
              sx={{
                bgcolor: '#EDF5F1',
                border: '1px solid #D1E5DB',
                borderRadius: '16px',
                p: 1,
                px: 1.25,
                mb: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: '#FFFFFF',
                  color: '#063937',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <StorefrontIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.76rem', fontWeight: 750, color: '#0F172A', lineHeight: 1.2 }}>
                  Book via partner
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.68rem',
                    color: '#475569',
                    textDecoration: 'underline',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  🔗 {previewData.website ? previewData.website.replace(/^https?:\/\//, '') : 'partner.example.com/ref/ab...'}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  if (previewData.website) {
                    const url = previewData.website.startsWith('http') ? previewData.website : `https://${previewData.website}`;
                    window.open(url, '_blank');
                  }
                }}
                sx={{
                  bgcolor: '#063937',
                  color: '#FFFFFF',
                  borderRadius: '999px',
                  px: 1.8,
                  py: 0.5,
                  fontSize: '0.74rem',
                  fontWeight: 800,
                  textTransform: 'none',
                  flexShrink: 0,
                  '&:hover': { bgcolor: '#042725' },
                }}
              >
                Open &gt;
              </Button>
            </Box>

            {/* Bottom Action Row: Buy Insurance / Main CTA + Chat + WhatsApp */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                fullWidth
                variant="contained"
                sx={{
                  bgcolor: '#063937',
                  color: '#FFFFFF',
                  borderRadius: '14px',
                  py: 1.1,
                  fontWeight: 800,
                  fontSize: '0.82rem',
                  textTransform: 'none',
                  flex: 1,
                  '&:hover': { bgcolor: '#042725' },
                }}
              >
                {previewData.callToAction && previewData.callToAction.length < 20
                  ? previewData.callToAction
                  : (previewData.pricingInformation?.plans?.[0]?.title ? `Buy ${previewData.pricingInformation.plans[0].title}` : 'Buy Insurance')}
              </Button>

              <Button
                variant="outlined"
                startIcon={<ChatIcon sx={{ fontSize: 16 }} />}
                sx={{
                  bgcolor: '#FFFFFF',
                  borderColor: '#E2E8F0',
                  color: '#0F172A',
                  borderRadius: '14px',
                  py: 1.1,
                  px: 2,
                  fontWeight: 800,
                  fontSize: '0.78rem',
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: '#F8FAFC', borderColor: '#CBD5E1' },
                }}
              >
                Chat
              </Button>

              <Box
                onClick={() => {
                  if (previewData.whatsapp) {
                    const phoneClean = previewData.whatsapp.replace(/\D/g, '');
                    window.open(`https://wa.me/${phoneClean}`, '_blank');
                  }
                }}
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: '#25D366',
                  color: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                  boxShadow: '0 4px 12px rgba(37, 211, 102, 0.4)',
                  transition: 'transform 0.15s ease',
                  '&:hover': { transform: 'scale(1.05)', bgcolor: '#22C55E' },
                }}
              >
                <WhatsApp sx={{ fontSize: 26 }} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Dialog>

      {/* Card View Preview Dialog (Mobile Listing Card Preview) */}
      <Dialog
        open={cardViewDialogOpen}
        onClose={handleCloseCardView}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: 520,
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }
        }}
      >
        {/* Cross (Close) Icon positioned at top-right */}
        <Box sx={{ width: '100%', maxWidth: 480, display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <IconButton
            onClick={handleCloseCardView}
            size="small"
            sx={{
              color: '#ffffff',
              bgcolor: 'rgba(0, 0, 0, 0.45)',
              backdropFilter: 'blur(4px)',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.75)' },
            }}
          >
            <CloseRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* App-Side Service Listing Card (Exact Match to Mobile App Screenshot) */}
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 480,
            borderRadius: '24px',
            bgcolor: '#ffffff',
            border: cardViewData.isVerified ? '1.5px solid #A7F3D0' : '1px solid #E2E8F0',
            boxShadow: '0 20px 45px rgba(0, 0, 0, 0.28)',
              position: 'relative',
              overflow: 'hidden',
              p: 2.5,
              pt: 2.2,
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.2s ease',
            }}
          >
            {/* Top-Right Corner Tab (TEST / CTA) */}
            {cardViewData.callToAction && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bgcolor: '#FF6B4A',
                  color: '#FFFFFF',
                  px: 2.2,
                  py: 0.65,
                  borderRadius: '0 24px 0 16px',
                  fontWeight: 800,
                  fontSize: '0.8rem',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  zIndex: 2,
                }}
              >
                {cardViewData.callToAction}
              </Box>
            )}

            {/* Top-Left Pill: VERIFIED */}
            <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
              {cardViewData.isVerified ? (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.6,
                    bgcolor: '#D1FAE5',
                    color: '#059669',
                    px: 1.5,
                    py: 0.45,
                    borderRadius: '999px',
                    fontWeight: 800,
                    fontSize: '0.78rem',
                    letterSpacing: '0.04em',
                  }}
                >
                  <CheckCircle sx={{ fontSize: 16, color: '#059669' }} />
                  VERIFIED
                </Box>
              ) : (
                <Box sx={{ height: 26 }} />
              )}
            </Box>

            {/* Middle Content: Thumbnail on left, All details on right */}
            <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
              {/* Square Thumbnail */}
              <Box
                sx={{
                  width: 90,
                  height: 90,
                  minWidth: 90,
                  borderRadius: '18px',
                  overflow: 'hidden',
                  bgcolor: '#F8FAFC',
                  border: '1px solid #E2E8F0',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {cardViewData.thumbnailUrl ? (
                  <Box
                    component="img"
                    src={cardViewData.thumbnailUrl}
                    alt={cardViewData.title}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      bgcolor: '#063937',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: '1.8rem',
                    }}
                  >
                    {cardViewData.avatarInitials}
                  </Box>
                )}
              </Box>

              {/* Right Column: Service Mode Badges, Title, Ratings, Address, District */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Service Mode Badges Row (Online / In Person) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                  {cardViewData.isOnline && (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.4,
                        px: 1.2,
                        py: 0.35,
                        borderRadius: '10px',
                        bgcolor: '#F0FDF4',
                        border: '1px solid #BBF7D0',
                        color: '#16A34A',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                      }}
                    >
                      <VideocamIcon sx={{ fontSize: 15, color: '#16A34A' }} />
                      Online
                    </Box>
                  )}
                  {cardViewData.isInPerson && (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.4,
                        px: 1.2,
                        py: 0.35,
                        borderRadius: '10px',
                        bgcolor: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                        color: '#0F172A',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                      }}
                    >
                      <StorefrontIcon sx={{ fontSize: 14, color: '#0F172A' }} />
                      In Person
                    </Box>
                  )}
                </Box>

                {/* Title + Verified Checkmark + Chevron */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.75 }}>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: '1.25rem',
                      color: '#063937',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {cardViewData.title}
                  </Typography>
                  {cardViewData.isVerified && (
                    <CheckCircle sx={{ color: '#10B981', fontSize: 18, flexShrink: 0 }} />
                  )}
                  <ChevronRightIcon sx={{ color: '#94A3B8', fontSize: 22, ml: 'auto', flexShrink: 0 }} />
                </Box>

                {/* Ratings Row (In-App Rating + Google Maps Rating - Exact Match to Screenshot) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.75 }}>
                  {/* Rating 1: In-App Rating */}
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.45,
                      bgcolor: '#FFFDF0',
                      border: '1px solid #FDE68A',
                      borderRadius: '999px',
                      px: 1.1,
                      py: 0.25,
                    }}
                  >
                    <svg width="11" height="12" viewBox="0 0 18 20" fill="none" style={{ flexShrink: 0 }}>
                      <path d="M3 18V9C3 5.686 5.686 3 9 3C12.314 3 15 5.686 15 9V18" stroke="#063937" strokeWidth="3.5" strokeLinecap="round"/>
                      <circle cx="16" cy="16" r="2.2" fill="#EF4444"/>
                    </svg>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#D97706', lineHeight: 1 }}>
                      {cardViewData.ratingInfo && cardViewData.ratingInfo.hasRating ? cardViewData.ratingInfo.averageRating.toFixed(1) : '4.0'}
                    </Typography>
                    <StarIcon sx={{ fontSize: 11, color: '#F59E0B' }} />
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 500, color: '#64748B', lineHeight: 1 }}>
                      ({cardViewData.ratingInfo && cardViewData.ratingInfo.hasRating ? (cardViewData.ratingInfo.totalRating || 1) : 1})
                    </Typography>
                  </Box>

                  {/* Rating 2: Google Maps Rating */}
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.45,
                      bgcolor: '#F0F9FF',
                      border: '1px solid #BAE6FD',
                      borderRadius: '999px',
                      px: 1.1,
                      py: 0.25,
                    }}
                  >
                    <svg width="10" height="13" viewBox="0 0 24 32" fill="none" style={{ flexShrink: 0 }}>
                      <path d="M12 0C5.373 0 0 5.373 0 12c0 9.25 12 20 12 20s12-10.75 12-20c0-6.627-5.373-12-12-12z" fill="#EA4335"/>
                      <circle cx="12" cy="11" r="4.5" fill="#FFFFFF"/>
                    </svg>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#0284C7', lineHeight: 1 }}>
                      4.5
                    </Typography>
                    <StarIcon sx={{ fontSize: 11, color: '#0284C7' }} />
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 500, color: '#64748B', lineHeight: 1 }}>
                      (120)
                    </Typography>
                  </Box>
                </Box>

                {/* Location Pin & Full Address */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.75 }}>
                  <LocationOn sx={{ color: '#063937', fontSize: 16, mt: 0.15, flexShrink: 0 }} />
                  <Typography
                    sx={{
                      fontSize: '0.8rem',
                      color: '#334155',
                      fontWeight: 500,
                      lineHeight: 1.35,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {cardViewData.addressText || cardViewData.address}
                  </Typography>
                </Box>

                {/* District Pill */}
                {cardViewData.districtName && (
                  <Box>
                    <Box
                      sx={{
                        display: 'inline-block',
                        bgcolor: '#EDF5F1',
                        color: '#063937',
                        borderRadius: '10px',
                        px: 1.5,
                        py: 0.35,
                        fontSize: '0.8rem',
                        fontWeight: 800,
                        textTransform: 'capitalize',
                      }}
                    >
                      {cardViewData.districtName}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Bottom Subcategory Pills & Distance Row */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                mt: 2,
                pt: 1.5,
                borderTop: '1px solid #F1F5F9',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                  overflowX: 'auto',
                  gap: 0.75,
                  flex: 1,
                  minWidth: 0,
                  py: 0.25,
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                  msOverflowStyle: 'none',
                }}
              >
                {cardViewData.subCatChips && cardViewData.subCatChips.map(({ key, label }) => (
                  <Box
                    key={key}
                    sx={{
                      borderRadius: '999px',
                      bgcolor: '#F8FAFC',
                      border: '1.5px solid #E2E8F0',
                      px: 1.6,
                      py: 0.45,
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      color: '#0F172A',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {label}
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  borderRadius: '10px',
                  bgcolor: '#F8FAFC',
                  px: 1.4,
                  py: 0.45,
                  ml: 'auto',
                  flexShrink: 0,
                }}
              >
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: '#0F172A', whiteSpace: 'nowrap' }}>
                  {cardViewData.distance || '25.9 km'}
                </Typography>
              </Box>
            </Box>
          </Paper>

        {/* Switch to Full Mobile Screen Button */}
        <Button
          fullWidth
          variant="contained"
          size="small"
          onClick={() => {
            handleCloseCardView();
            handleOpenPreview(cardViewContact);
          }}
          endIcon={<SmartphoneIcon sx={{ fontSize: 16 }} />}
          sx={{
            mt: 2,
            maxWidth: 480,
            borderRadius: '14px',
            bgcolor: '#063937',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.85rem',
            py: 1.1,
            boxShadow: '0 8px 24px rgba(6, 57, 55, 0.35)',
            '&:hover': { bgcolor: '#042725' },
          }}
        >
          Open Full Mobile Screen
        </Button>
      </Dialog>
      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onClose={closeApprovalDialog}>
        <DialogTitle>
          {getApprovalDialogTitle(approvalStatus)}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {getApprovalDialogBodyText(approvalStatus)}
          </DialogContentText>
          {approvalStatus === 'DECLINE' && (
            <TextField autoFocus margin="dense" name="approvalNote" label={t('pages.contacts.approval.reasonDecline')} type="text" fullWidth variant="outlined" value={approvalNote} onChange={(e) => setApprovalNote(e.target.value)} disabled={isSubmitting} required error={requiresDeclineApprovalNote(approvalStatus, approvalNote)} helperText={requiresDeclineApprovalNote(approvalStatus, approvalNote) ? t('pages.contacts.approval.reasonRequired') : ''} sx={{ mt: 2 }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApprovalDialog} disabled={isSubmitting}>{t('common.cancel')}</Button>
          <Button onClick={handleApproval} variant="contained" color={getApprovalButtonColor(approvalStatus)} disabled={isSubmitting || requiresDeclineApprovalNote(approvalStatus, approvalNote)} startIcon={isSubmitting ? <CircularProgress size={20} /> : null}>
            {getApprovalButtonLabel(isSubmitting, approvalStatus)}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dedicated Call to Action Edit Popup Dialog */}
      <Dialog
        open={ctaDialogOpen}
        onClose={closeCtaDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Campaign sx={{ color: '#FF8C42' }} />
          {t('pages.contacts.form.callToAction') || 'Call to Action'}
        </DialogTitle>
        <DialogContent dividers>
          {ctaDialogContact && (
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1.5 }}>
              {ctaDialogContact.title}
            </Typography>
          )}
          <TextField
            autoFocus
            fullWidth
            label={t('pages.contacts.form.callToAction') || 'Call to Action'}
            placeholder={t('pages.contacts.form.callToActionPlaceholder') || 'e.g. Click here, Register...'}
            value={ctaDialogValue}
            onChange={(e) => setCtaDialogValue(e.target.value)}
            inputProps={{ maxLength: 30 }}
            helperText={`${(ctaDialogValue || '').length}/30`}
            disabled={ctaDialogSubmitting}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={closeCtaDialog}
            disabled={ctaDialogSubmitting}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveCtaDialog}
            disabled={ctaDialogSubmitting}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#FF8C42',
              '&:hover': { bgcolor: '#E06526' },
            }}
          >
            {ctaDialogSubmitting ? (
              <CircularProgress size={22} sx={{ color: '#fff' }} />
            ) : (
              t('common.save') || 'Save'
            )}
          </Button>
        </DialogActions>
      </Dialog>


    </>
  );
};

export default Contacts;
